import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";
import { HiArrowSmLeft, HiSearch, HiPlus, HiX } from "react-icons/hi";
import { debounce } from "lodash";
import FileUpload from "../common/FileUpload";
import SearchableDropdown from "../common/SearchableDropdown";
import FormField, { inputCls } from "./FormField";
import VariantRow from "./VariantRow";
import VariantTable from "./VariantTable";
import InfoTooltip from "../common/InfoTooltip";
import ToggleSwitch from "../common/ToggleSwitch";

// ─── STAGES ──────────────────────────────────────────────────────────────────
const STAGES = {
  SEARCH: "search",
  CATEGORY_SELECTION: "category_selection",
  PRODUCT_LIST: "product_list",
  NEW_PRODUCT_FORM: "new_product_form",
};

// ─── Initial form state ───────────────────────────────────────────────────────
const INITIAL_FORM = {
  name: "",
  unit: "",
  packaging: "",
  sku_number: "",
  brand: "",
  manufacturer: "",
  hsn_code: "",
  photos: null,
  mrp: "",
  purchase_price: "",
  gst_slab: "",
  openingInventory: "",
  length: "",
  width: "",
  height: "",
  weight: "",
  colour: "",
  trackInventory: true,
  restockAlert: "",
  description: "",
  category: "",
  subcategory: "",
  has_variants: false,
  barcode: "",
  is_expiry_tracked: true,
};

// ─── Attribute & Options Manager ─────────────────────────────────────────────
const AttributeManager = ({ attributes, onAttributesChange }) => {
  // State for current option input
  const [currentOption, setCurrentOption] = useState({});
  const inputRefs = useRef({});

  // Add a new attribute
  const addAttribute = () => {
    const newAttr = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
      name: '',
      options: [],
      is_variant_attribute: true,
    };
    onAttributesChange([...attributes, newAttr]);
  };

  // Remove an attribute
  const removeAttribute = (id) => {
    if (attributes.length <= 1) {
      toast.warning("Product must have at least one attribute for variants");
      return;
    }
    onAttributesChange(attributes.filter(a => a.id !== id));
  };

  // Update attribute name
  const updateAttributeName = (id, name) => {
    onAttributesChange(attributes.map(a => 
      a.id === id ? { ...a, name } : a
    ));
  };

  // Add option to attribute (from input)
  const handleAddOption = (attrId, value) => {
    if (!value || !value.trim()) return;
    const trimmedValue = value.trim();
    
    const attr = attributes.find(a => a.id === attrId);
    if (attr && attr.options.includes(trimmedValue)) {
      toast.warning(`Option "${trimmedValue}" already exists`);
      return;
    }
    
    onAttributesChange(attributes.map(a => 
      a.id === attrId ? { ...a, options: [...a.options, trimmedValue] } : a
    ));
    setCurrentOption(prev => ({ ...prev, [attrId]: '' }));
  };

  // Remove option from attribute
  const removeOption = (attrId, optionToRemove) => {
    onAttributesChange(attributes.map(a => 
      a.id === attrId ? { ...a, options: a.options.filter(opt => opt !== optionToRemove) } : a
    ));
  };

  // Handle Enter key press
  const handleKeyDown = (attrId, e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption(attrId, currentOption[attrId] || '');
    }
  };

  // Handle Backspace key - remove last option if input is empty
  const handleKeyUp = (attrId, e) => {
    if (e.key === 'Backspace' && !currentOption[attrId]) {
      const attr = attributes.find(a => a.id === attrId);
      if (attr && attr.options.length > 0) {
        const lastOption = attr.options[attr.options.length - 1];
        removeOption(attrId, lastOption);
      }
    }
  };

  // Focus input when clicking on the container
  const handleContainerClick = (attrId) => {
    if (inputRefs.current[attrId]) {
      inputRefs.current[attrId].focus();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">Variations</p>
      </div>

      {attributes.length === 0 ? (
        <div className="border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-500 text-sm">No attributes defined</p>
          <button
            onClick={addAttribute}
            className="mt-3 text-blue-600 text-sm hover:underline flex items-center gap-1 mx-auto"
          >
            <HiPlus className="w-4 h-4" /> Add Attribute
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {attributes.map((attr) => (
            <div key={attr.id} className="flex items-center gap-2 flex-wrap">
              {/* Attribute Name */}
              <input
                type="text"
                value={attr.name}
                onChange={(e) => updateAttributeName(attr.id, e.target.value)}
                placeholder="e.g. color"
                className="text-sm w-36 px-2 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              
              {/* Option Input - Clean like Zoho, only pills visible */}
              <div 
                className="flex items-center flex-wrap gap-1 px-2 py-1 border border-gray-300 rounded-md flex-1 min-w-[200px] cursor-text hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500"
                onClick={() => handleContainerClick(attr.id)}
              >
                {/* Options as pills */}
                {attr.options.map((option) => (
                  <div 
                    key={option} 
                    className="flex items-center bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 gap-0.5"
                  >
                    <span className="text-xs text-blue-700">{option}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeOption(attr.id, option);
                      }}
                      className="text-blue-400 hover:text-red-500"
                    >
                      <HiX className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                
                {/* Invisible input - shows cursor when focused */}
                <input
                  ref={(el) => {
                    if (el) inputRefs.current[attr.id] = el;
                  }}
                  type="text"
                  value={currentOption[attr.id] || ''}
                  onChange={(e) => setCurrentOption(prev => ({ ...prev, [attr.id]: e.target.value }))}
                  onKeyDown={(e) => handleKeyDown(attr.id, e)}
                  onKeyUp={(e) => handleKeyUp(attr.id, e)}
                  placeholder={
                  attr.options.length > 0
                    ? ''
                    : attr.name?.trim()
                      ? `Type a${/^[aeiou]/i.test(attr.name.trim()) ? 'n' : ''} ${attr.name.trim()} options and press Enter`
                      : 'Type and press Enter'
                }
                  className="flex-1 min-w-[40px] outline-none bg-transparent text-sm py-0.5"
                  style={{ caretColor: '#3b82f6' }}
                />
              </div>
              
              {/* Remove Attribute */}
              <button
                onClick={() => removeAttribute(attr.id)}
                className="text-gray-400 hover:text-red-500 p-1"
              >
                <HiX className="w-4 h-4" />
              </button>
            </div>
          ))}

          {/* Add more attributes */}
          <button
            onClick={addAttribute}
            className="text-blue-600 text-sm hover:underline flex items-center gap-1"
          >
            <HiPlus className="w-4 h-4" /> Add more attributes
          </button>
        </div>
      )}
    </div>
  );
};

// Numeric retail / internal codes only. Names and SKUs stay out of the barcode field.
// Accepts numeric retail codes (8901234567890) AND alphanumeric internal codes
// (B0753H9768, ABC1234567) produced by Code 128 / Code 39 scanners.
// Rejects spaces and requires at least 4 digits, so plain names like "coke"
// or "Drools Dog Food" don't get treated as barcodes.
export const looksLikeBarcode = (value) => {
  const raw = String(value || "").trim();
  if (!raw || /\s/.test(raw)) return false;
  const compact = raw.replace(/[^0-9a-z]/gi, "");
  if (compact.length < 6) return false;
  const digitCount = (compact.match(/\d/g) || []).length;
  return digitCount >= 4;
};

const compactCode = (value) =>
  String(value || "")
    .replace(/[^0-9a-z]/gi, "")
    .toLowerCase();

const idOf = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") return entry;
  return entry._id || entry.id || null;
};

const findMasterByBarcode = (products, code) => {
  const target = compactCode(code);
  if (!target) return null;
  for (const product of products || []) {
    if (compactCode(product?.barcode) === target) {
      return { product, variant: null };
    }
    const variant = (product?.variants || []).find(
      (v) => compactCode(v?.barcode) === target,
    );
    if (variant) return { product, variant };
  }
  return null;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ProductDrawer = ({
  isOpen,
  onClose,
  onSave,
  productData = null,
  inventoryId = null,
  newProductName = "",
  newProductIndex = null,
  initialBarcode = "",
  forceZeroOpeningInventory = false,
  vendorId,
}) => {
  const isEditMode = !!productData && !!inventoryId;
  const searchInputRef = useRef(null);

  // ── State ──────────────────────────────────────────────────────────────────
  const [currentStage, setCurrentStage] = useState(STAGES.NEW_PRODUCT_FORM);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [vendorSkus, setVendorSkus] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [allVendorProducts, setAllVendorProducts] = useState([]);

  // Product-name duplicate-check suggestions
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);

  // Attributes for variants (Zoho-style - defined at product level)
  const [variantAttributes, setVariantAttributes] = useState([]);

  // Auto-generate variants when attributes change
  const [generatedVariants, setGeneratedVariants] = useState([]);

  // Subcategory dropdown
  const [showSubCatDropdown, setShowSubCatDropdown] = useState(false);

  const [skuManuallyEdited, setSkuManuallyEdited] = useState(false);

  const generateSingleProductSku = (name) => {
    if (!name || !name.trim()) return "";
    const prefix = name.trim().substring(0, 3).toUpperCase();
    const randomSuffix = Math.random()
      .toString(36)
      .substring(2, 6)
      .toUpperCase();
    return `${prefix}-${randomSuffix}`;
  };

  // ✅ Per-variant-row validation errors: { [variantId]: { sku: true, mrp: true, unit: true } }
  const [variantErrors, setVariantErrors] = useState({});

  // ── Brands & Manufacturers State ──────────────────────────────────────────
  const [brands, setBrands] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingManufacturers, setLoadingManufacturers] = useState(false);
  const [brandSearch, setBrandSearch] = useState("");
  const [manufacturerSearch, setManufacturerSearch] = useState("");
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [showManufacturerDropdown, setShowManufacturerDropdown] =
    useState(false);
  const [creatingBrand, setCreatingBrand] = useState(false);
  const [creatingManufacturer, setCreatingManufacturer] = useState(false);

  // --- Vendor Taxes & Units State ---
  const [vendorTaxes, setVendorTaxes] = useState([]);
  const [vendorUnits, setVendorUnits] = useState([]);
  const [loadingTaxes, setLoadingTaxes] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [vendorHsns, setVendorHsns] = useState([]);
  const [loadingHsns, setLoadingHsns] = useState(false);
  const [hsnSearch, setHsnSearch] = useState("");
  const [showHsnDropdown, setShowHsnDropdown] = useState(false);

  const brandDropdownRef = useRef(null);
  const manufacturerDropdownRef = useRef(null);
  const hsnDropdownRef = useRef(null);

  // ── Fetch Brands & Manufacturers ──────────────────────────────────────────
  const fetchBrands = async () => {
    setLoadingBrands(true);
    try {
      const res = await httpService.get("/vendorproduct/getvendorbrands");
      const list = res.data || [];
      setBrands(list);
      return list;
    } catch (err) {
      console.error("Error fetching brands:", err);
      return [];
    } finally {
      setLoadingBrands(false);
    }
  };

  const nameMatches = useMemo(() => {
    const term = formData.name?.trim().toLowerCase();
    if (!term || term.length < 2) return [];
    return allVendorProducts
      .filter(
        (p) =>
          p.name?.toLowerCase().includes(term) && p._id !== productData?._id,
      )
      .slice(0, 5);
  }, [formData.name, allVendorProducts, productData]);

  const fetchManufacturers = async () => {
    setLoadingManufacturers(true);
    try {
      const res = await httpService.get(
        "/vendorproduct/getvendormanufacturers",
      );
      const list = res.data || [];
      setManufacturers(list);
      return list;
    } catch (err) {
      console.error("Error fetching manufacturers:", err);
      return [];
    } finally {
      setLoadingManufacturers(false);
    }
  };

  // ── Create Brand & Manufacturer ───────────────────────────────────────────
  const handleCreateBrand = async (name) => {
    if (!name?.trim()) return;
    setCreatingBrand(true);
    try {
      const res = await httpService.post("/vendorproduct/addvendorbrand", {
        name: name.trim(),
        is_active: true,
      });
      const brandId = res.data?.data?._id || res.data?._id;
      if (brandId) {
        await fetchBrands();
        setFormData((prev) => ({ ...prev, brand: brandId }));
        setBrandSearch("");
        setShowBrandDropdown(false);
        toast.success("Brand created successfully");
      }
    } catch (err) {
      console.error("Error creating brand:", err);
      toast.error("Failed to create brand");
    } finally {
      setCreatingBrand(false);
    }
  };

  const handleCreateManufacturer = async (name) => {
    if (!name?.trim()) return;
    setCreatingManufacturer(true);
    try {
      const res = await httpService.post(
        "/vendorproduct/addvendormanufacturer",
        {
          name: name.trim(),
          is_active: true,
        },
      );
      const mfgId = res.data?.data?._id || res.data?._id;
      if (mfgId) {
        await fetchManufacturers();
        setFormData((prev) => ({ ...prev, manufacturer: mfgId }));
        setManufacturerSearch("");
        setShowManufacturerDropdown(false);
        toast.success("Manufacturer created successfully");
      }
    } catch (err) {
      console.error("Error creating manufacturer:", err);
      toast.error("Failed to create manufacturer");
    } finally {
      setCreatingManufacturer(false);
    }
  };

  const handleDeleteBrand = async (brandId, e) => {
    e?.stopPropagation(); // prevent selecting the option while deleting it
    if (!window.confirm("Delete this brand? This cannot be undone.")) return;
    try {
      await httpService.delete(`/vendorproduct/deletevendorbrand/${brandId}`);
      await fetchBrands();
      // If the deleted brand was selected, clear the selection
      if (formData.brand === brandId) {
        setFormData((prev) => ({ ...prev, brand: "" }));
      }
      toast.success("Brand deleted successfully");
    } catch (err) {
      console.error("Error deleting brand:", err);
      toast.error(err.response?.data?.message || "Failed to delete brand");
    }
  };

  const handleDeleteManufacturer = async (mfgId, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this manufacturer? This cannot be undone."))
      return;
    try {
      await httpService.delete(
        `/vendorproduct/deletevendormanufacturer/${mfgId}`,
      );
      await fetchManufacturers();
      if (formData.manufacturer === mfgId) {
        setFormData((prev) => ({ ...prev, manufacturer: "" }));
      }
      toast.success("Manufacturer deleted successfully");
    } catch (err) {
      console.error("Error deleting manufacturer:", err);
      toast.error(
        err.response?.data?.message || "Failed to delete manufacturer",
      );
    }
  };

  // ── Derived: subcategories ────────────────────────────────────────────────
  // --- Fetch & Manage Vendor Taxes & Units ---
  const fetchVendorTaxes = async () => {
    setLoadingTaxes(true);
    try {
      const res = await httpService.get("/vendorproduct/getvendortaxes");
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
      setVendorTaxes(Array.isArray(list) ? list : []);
      return list;
    } catch (err) {
      console.error("Error fetching vendor taxes:", err);
      return [];
    } finally {
      setLoadingTaxes(false);
    }
  };

  const fetchVendorUnits = async () => {
    setLoadingUnits(true);
    try {
      const res = await httpService.get("/vendorproduct/getvendorunits");
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
      setVendorUnits(Array.isArray(list) ? list : []);
      return list;
    } catch (err) {
      console.error("Error fetching vendor units:", err);
      return [];
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleCreateVendorTax = async (rate, name) => {
    try {
      const res = await httpService.post("/vendorproduct/addvendortax", {
        rate: Number(rate),
        name: name || `GST ${rate}%`,
        tax_type: "GST",
        is_active: true,
      });
      await fetchVendorTaxes();
      return res.data;
    } catch (err) {
      console.error("Error creating vendor tax:", err);
      toast.error("Failed to create tax");
    }
  };

  const fetchVendorHsns = async () => {
    setLoadingHsns(true);
    try {
      const res = await httpService.get("/vendorproduct/getvendorhsns");
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
      setVendorHsns(Array.isArray(list) ? list : []);
      return list;
    } catch (err) {
      console.error("Error fetching vendor HSNs:", err);
      return [];
    } finally {
      setLoadingHsns(false);
    }
  };

  const handleHsnChange = (codeVal) => {
    handleChange("hsn_code", codeVal);
    if (codeVal && vendorHsns.length > 0) {
      const trimmed = String(codeVal).trim();
      const matched = vendorHsns.find((h) => String(h.hsn_code).trim() === trimmed);
      if (matched && matched.gst_rate !== undefined && matched.gst_rate !== null) {
        handleChange("gst_slab", String(matched.gst_rate));
      }
    }
  };

  const hsnOptions = useMemo(() => {
    const list = vendorHsns.map((h) => ({
      _id: String(h.hsn_code).trim(),
      name: `${h.hsn_code} - ${h.description || "HSN"} (${h.gst_rate}% GST)`,
      hsn_code: String(h.hsn_code).trim(),
      gst_rate: h.gst_rate,
    }));
    if (
      formData.hsn_code &&
      !list.some((opt) => opt._id === String(formData.hsn_code).trim())
    ) {
      list.unshift({
        _id: String(formData.hsn_code).trim(),
        name: String(formData.hsn_code).trim(),
        hsn_code: String(formData.hsn_code).trim(),
      });
    }
    return list;
  }, [vendorHsns, formData.hsn_code]);

  const handleCreateVendorUnit = async (name, symbol, category) => {
    try {
      const res = await httpService.post("/vendorproduct/addvendorunit", {
        name: name.trim(),
        symbol: symbol || name.trim().toLowerCase(),
        category: category || "count",
        is_active: true,
      });
      await fetchVendorUnits();
      return res.data;
    } catch (err) {
      console.error("Error creating vendor unit:", err);
      toast.error("Failed to create unit");
    }
  };

  const subcategories = useMemo(
    () =>
      categories.find((c) => c._id === selectedCategory)?.sub_category || [],
    [categories, selectedCategory],
  );

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = useCallback(
    (field, value, currentForm = formData) => {
      const merged = { ...currentForm, [field]: value };
      const newErrors = { ...errors };

      switch (field) {
        case "name":
          if (!value?.trim()) {
            newErrors.name = "Product Name is required";
            setTouched((prev) => ({ ...prev, name: true }));
          } else if (value.length < 3) {
            newErrors.name = "Name must be at least 3 characters";
            setTouched((prev) => ({ ...prev, name: true }));
          } else delete newErrors.name;
          break;
        case "sku_number":
          if (!merged.has_variants && !value?.trim()) {
            newErrors.sku_number = "SKU Number is required";
            setTouched((prev) => ({ ...prev, sku_number: true }));
          } else delete newErrors.sku_number;
          break;
        case "mrp":
          if (!merged.has_variants) {
            if (!value || Number(value) <= 0) {
              newErrors.mrp = "MRP must be greater than 0";
              setTouched((prev) => ({ ...prev, mrp: true }));
            } else delete newErrors.mrp;
          } else {
            delete newErrors.mrp;
          }
          break;
        case "unit": // ✅ ADD THIS CASE
          if (!merged.has_variants && !value?.trim()) {
            newErrors.unit = "Pack Unit is required";
            setTouched((prev) => ({ ...prev, unit: true }));
          } else {
            delete newErrors.unit;
          }
          break;
        case "hsn_code":
          // ✅ GST is now mandatory, so HSN is always required
          if (!value?.trim()) {
            newErrors.hsn_code = "HSN Code is required";
            setTouched((prev) => ({ ...prev, hsn_code: true }));
          } else {
            delete newErrors.hsn_code;
          }
          break;
        case "gst_slab":
          // ✅ GST Slab is now mandatory
          if (!value || Number(value) < 0) {
            newErrors.gst_slab = "GST Slab is required";
            setTouched((prev) => ({ ...prev, gst_slab: true }));
          } else {
            delete newErrors.gst_slab;
          }
          break;
        default:
          break;
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [errors, formData],
  );

  const highlightMissingFields = useCallback((missingFields) => {
    // Clear any previous highlights first
    document.querySelectorAll(".field-error-highlight").forEach((el) => {
      el.classList.remove(
        "field-error-highlight",
        "border-red-500",
        "ring-2",
        "ring-red-200",
      );
    });

    // Map field names to element IDs or selectors with better matching
    const fieldMap = {
      "Product Name": ["name", "product-name", "productName"],
      Category: ["category", "selectedCategory"],
      "SKU Number": ["sku_number", "sku-number", "sku"],
      MRP: ["mrp", "mrp-input"],
      "HSN Code": ["hsn_code", "hsn-code", "hsn"],
      "Pack Unit": ["unit"], // ✅ Add this mapping
      "SKU Number (must be greater than 0)": [
        "sku_number",
        "sku-number",
        "sku",
      ],
      "MRP (must be greater than 0)": ["mrp", "mrp-input"],
    };

    missingFields.forEach((field) => {
      // Find matching field keys
      let matchedKey = null;
      for (const [key, ids] of Object.entries(fieldMap)) {
        if (field.includes(key) || key.includes(field)) {
          matchedKey = key;
          break;
        }
      }

      if (matchedKey) {
        const ids = fieldMap[matchedKey];
        let element = null;

        // Try each possible ID/selector - LOOK FOR BOTH INPUT AND SELECT
        for (const id of ids) {
          element =
            document.querySelector(`[name="${id}"]`) ||
            document.querySelector(`#${id}`) ||
            document.querySelector(`input[placeholder*="${id}"]`) ||
            document.querySelector(`select[id="${id}"]`) || // ✅ Check for select
            document.querySelector(`select[name="${id}"]`) || // ✅ Check for select by name
            document.querySelector(`[data-field="${id}"]`);
          if (element) break;
        }

        // If still not found, try to find by label text
        if (!element) {
          const labels = document.querySelectorAll("label");
          for (const label of labels) {
            if (
              label.textContent
                ?.toLowerCase()
                .includes(
                  field.toLowerCase().split("(")[0].trim().toLowerCase(),
                )
            ) {
              const forAttr = label.getAttribute("for");
              if (forAttr) {
                element = document.getElementById(forAttr);
                if (element) break;
              }
              // Check next sibling or parent (this will catch select elements too)
              const nextElement =
                label.nextElementSibling?.querySelector("input, select");
              if (nextElement) {
                element = nextElement;
                break;
              }
            }
          }
        }

        if (element) {
          // Add highlight class
          element.classList.add(
            "field-error-highlight",
            "border-red-500",
            "ring-2",
            "ring-red-200",
            "transition-all",
            "duration-300",
          );

          // Add a small error indicator
          const parent =
            element.closest(".form-field-group") || element.parentElement;
          if (parent && !parent.querySelector(".field-error-indicator")) {
            const indicator = document.createElement("span");
            indicator.className =
              "field-error-indicator ml-2 text-red-500 text-xs";
            parent.appendChild(indicator);
          }

          // Scroll to the element
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus({ preventScroll: true });
        }
      }
    });
  }, []);

  const areRequiredFieldsFilled = useCallback(() => {
    if (!formData.name?.trim() || formData.name.length < 3) return false;

    // if (!selectedCategory) return false;

    // ✅ GST Slab is mandatory
    if (!formData.gst_slab || Number(formData.gst_slab) < 0) return false;

    // ✅ HSN Code is mandatory
    if (!formData.hsn_code?.trim()) return false;

    // For normal product without variants
    if (!formData.has_variants) {
      if (!formData.sku_number?.trim()) return false;
      if (!formData.mrp || Number(formData.mrp) <= 0) return false;
    } else {
      // For product with variants - check attributes and options
      const validAttrs = variantAttributes.filter(
        (a) => a.name && a.options.some((o) => o.trim()),
      );
      if (validAttrs.length === 0) {
        return false; // No attributes defined
      }
      // Check each attribute has at least one option
      for (const attr of validAttrs) {
        if (attr.options.filter((o) => o.trim()).length === 0) {
          return false;
        }
      }

      // ✅ Every generated variant row must have SKU, MRP, and Pack Unit filled
      const variantsToCheck =
        generatedVariants.length > 0
          ? generatedVariants
          : generateVariantsFromAttributes();

      if (variantsToCheck.length === 0) return false;

      for (const v of variantsToCheck) {
        if (!v.sku?.trim()) return false;
        if (!v.mrp || Number(v.mrp) <= 0) return false;
        if (!v.unit?.trim()) return false;
      }
    }

    if (
      formData.gst_slab &&
      Number(formData.gst_slab) > 0 &&
      !formData.hsn_code?.trim()
    )
      return false;

    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, variantAttributes, generatedVariants]);

  const hasFormErrors = useCallback(
    () => Object.keys(errors).length > 0,
    [errors],
  );
  const isFormValid = useCallback(
    () => !hasFormErrors() && areRequiredFieldsFilled(),
    [hasFormErrors, areRequiredFieldsFilled],
  );

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleChange = useCallback(
    (field, value) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      validate(field, value);
    },
    [validate],
  );

  const handleBlur = useCallback(
    (field) => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      validate(field, formData[field]);
    },
    [validate, formData],
  );

  const handleToggleVariants = (val) => {
    setFormData((prev) => ({
      ...prev,
      has_variants: val,
      ...(val ? { sku_number: "", barcode: "", mrp: "" } : {}),
    }));
    setVariantErrors({}); // ✅ reset variant-row highlights when switching item type
    setSkuManuallyEdited(false); // ✅ re-enable SKU auto-generation for the new item type
    if (val && variantAttributes.length === 0) {
      // Initialize with one empty attribute
      setVariantAttributes([
        {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
          name: "",
          options: [],
          is_variant_attribute: true,
        },
      ]);
    }
  };

  const buildDimension = (form = formData) =>
    [form.length, form.width, form.height].filter(Boolean).join(" x ") || "";

  const parseDimension = (dim = "") => {
    const parts = dim.split(/[xX*,]/).map((s) => s.trim());
    return {
      length: parts[0] || "",
      width: parts[1] || "",
      height: parts[2] || "",
    };
  };

  // ── Generate variants from attributes (Cartesian product) ──────────────────

  const generateVariantsFromAttributes = useCallback(() => {
    const validAttrs = variantAttributes.filter(
      (a) => a.name && a.options.some((o) => o.trim()),
    );
    if (validAttrs.length === 0) return [];

    const generateCombos = (index, current) => {
      if (index === validAttrs.length) {
        result.push([...current]);
        return;
      }
      const attr = validAttrs[index];
      for (const option of attr.options.filter((o) => o.trim())) {
        current.push({ attribute: attr, value: option });
        generateCombos(index + 1, current);
        current.pop();
      }
    };

    const result = [];
    generateCombos(0, []);

    return result.map((combo, index) => {
      const attrs = {};
      const itemNameParts = [formData.name];
      combo.forEach(({ attribute, value }) => {
        attrs[attribute.id] = value;
        itemNameParts.push(value);
      });

      // Generate SKU based on attribute values
      const skuParts = [formData.name.substring(0, 3).toUpperCase()];
      combo.forEach(({ value }) => {
        skuParts.push(value.substring(0, 3).toUpperCase());
      });
      skuParts.push(String(index + 1).padStart(3, "0"));

      return {
        id:
          Date.now().toString() +
          Math.random().toString(36).substr(2, 5) +
          Math.random().toString(36).substr(2, 3) +
          index,
        itemName: itemNameParts.join("-"),
        unit: formData.unit || "",
        packaging: formData.packaging || "",
        sku: skuParts.join("-"),
        barcode: `BAR-${String(index + 1).padStart(6, "0")}`,
        costPrice: "",
        mrp: "",
        openingStock: "",
        restockAlert: "", // NEW — per-variant reorder threshold
        isExpiryTracked: true,
        attributes: attrs, // { attr_id: value }
      };
    });
  }, [variantAttributes, formData.name, formData.unit, formData.packaging]);

  useEffect(() => {
    if (formData.has_variants && variantAttributes.length > 0) {
      const validAttrs = variantAttributes.filter(
        (a) => a.name && a.options.some((o) => o.trim()),
      );
      if (validAttrs.length > 0) {
        const newVariants = generateVariantsFromAttributes();
        setGeneratedVariants(newVariants);
      } else {
        setGeneratedVariants([]);
      }
    } else {
      setGeneratedVariants([]);
    }
  }, [
    variantAttributes,
    formData.has_variants,
    formData.name,
    generateVariantsFromAttributes,
  ]);

  // ✅ Auto-generate SKU for single (non-variant) products, same pattern as variant SKUs.
  // Skips edit mode (existing SKU stays as-is) and stops once the user edits it by hand.
  useEffect(() => {
    if (isEditMode) return;
    if (formData.has_variants) return;
    if (!formData.name?.trim()) return;
    if (skuManuallyEdited) return;

    const generatedSku = generateSingleProductSku(formData.name);
    setFormData((prev) => ({ ...prev, sku_number: generatedSku }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.name, formData.has_variants, skuManuallyEdited, isEditMode]);

  // ✅ Live-clear variant-row error highlights as the user fixes each field.
  // Runs whenever the variant rows change (e.g. user types a SKU, picks a unit, enters MRP).
  useEffect(() => {
    setVariantErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next = { ...prev };
      let changed = false;
      generatedVariants.forEach((v) => {
        if (!next[v.id]) return;
        const rowErrors = { ...next[v.id] };
        if (rowErrors.sku && v.sku?.trim()) {
          delete rowErrors.sku;
          changed = true;
        }
        if (rowErrors.mrp && v.mrp && Number(v.mrp) > 0) {
          delete rowErrors.mrp;
          changed = true;
        }
        if (rowErrors.unit && v.unit?.trim()) {
          delete rowErrors.unit;
          changed = true;
        }
        if (Object.keys(rowErrors).length === 0) {
          delete next[v.id];
        } else {
          next[v.id] = rowErrors;
        }
      });
      return changed ? next : prev;
    });
  }, [generatedVariants]);

  const buildPayload = (extra = {}) => {
    const basePayload = {
      name: formData.name,
      description: formData.description || "",
      packaging: formData.packaging ? formData.packaging.trim() : null,
      hsn_code: formData.hsn_code || "",
      gst_slab: formData.gst_slab || "",
      product_brand_id: formData.brand || null,
      manufacturer_id: formData.manufacturer || null,
      images: formData.photos || [],
      dimension: buildDimension(),
      weight: formData.weight || "",
      colour: formData.colour || "",
      track_inventory: formData.trackInventory,
      restock_alert: formData.restockAlert || "",
      is_expiry_tracked: !!formData.is_expiry_tracked,
      product_category: selectedCategory ? [selectedCategory] : [],
      product_subcategory: selectedSubcategory ? [selectedSubcategory] : [],
    };

    // If product has variants
    if (formData.has_variants) {
      // ✅ IMPORTANT: Use generatedVariants (which has user-edited values) instead of regenerating
      const variants =
        generatedVariants.length > 0
          ? generatedVariants
          : generateVariantsFromAttributes();

      // Build variant payload with attributes and user-entered values
      const variantPayload = variants.map((v) => ({
        variant_name: v.itemName || formData.name,
        unit: v.unit || formData.unit || "",
        packaging: v.packaging || formData.packaging || "",
        sku_number:
          v.sku ||
          `${formData.name.substring(0, 3).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`,
        barcode: v.barcode || "",
        purchase_price: Number(v.costPrice) || 0,
        mrp: Number(v.mrp) || 0,
        sale_price: Number(v.mrp) || 0,
        opening_stock: Number(v.openingStock) || 0,
        restock_alert:
          v.restockAlert !== "" && v.restockAlert != null
            ? Number(v.restockAlert)
            : null, // NEW
        track_inventory: v.restockAlert !== "" && v.restockAlert != null, // NEW — derived, no separate checkbox needed
        is_expiry_tracked: !!v.isExpiryTracked, // NEW
        attributes: Object.entries(v.attributes || {})
          .filter(
            ([_, value]) =>
              value !== "" && value !== null && value !== undefined,
          )
          .map(([attrId, value]) => {
            const attrDef = variantAttributes.find((a) => a.id === attrId);
            return { attribute_name: attrDef?.name || attrId, value };
          }),
      }));

      return {
        ...basePayload,
        has_variants: true,
        variant_attributes: variantAttributes
          .filter((a) => a.name)
          .map((a) => ({
            name: a.name,
            options: a.options.filter((o) => o.trim()),
          })),
        variants: variantPayload,
        sku_number: null,
        barcode: null,
        mrp: 0,
        sale_price: 0,
        ...extra,
      };
    }

    // Normal product without variants
    return {
      ...basePayload,
      unit: formData.unit || "",
      packaging: formData.packaging ? formData.packaging.trim() : null,
      sku_number: formData.sku_number,
      barcode: formData.barcode || "",
      mrp: Number(formData.mrp) || 0,
      sale_price: Number(formData.mrp) || 0,
      has_variants: false,
      ...extra,
    };
  };
  // ── Submit handlers ────────────────────────────────────────────────────────
  const validateFormAndGetErrors = useCallback(() => {
    const missingFields = [];
    const newTouched = { ...touched };

    // Check Product Name
    if (!formData.name?.trim() || formData.name.length < 3) {
      missingFields.push("Product Name (min 3 characters)");
      newTouched.name = true;
    }

    // Check Category
    // if (!selectedCategory) {
    //   missingFields.push("Category");
    // }

    // ✅ GST Slab is mandatory
    if (!formData.gst_slab || Number(formData.gst_slab) < 0) {
      missingFields.push("GST Slab");
      newTouched.gst_slab = true;
    }

    // ✅ HSN Code is mandatory
    if (!formData.hsn_code?.trim()) {
      missingFields.push("HSN Code");
      newTouched.hsn_code = true;
    }

    // For normal product without variants
    if (!formData.has_variants) {
      // ✅ No variants in play — clear any stale variant-row highlights
      setVariantErrors({});

      if (!formData.sku_number?.trim()) {
        missingFields.push("SKU Number");
        newTouched.sku_number = true;
      }
      if (!formData.mrp || Number(formData.mrp) <= 0) {
        missingFields.push("MRP (must be greater than 0)");
        newTouched.mrp = true;
      }
      if (!formData.unit?.trim()) {
        missingFields.push("Pack Unit");
        newTouched.unit = true;
      }
    } else {
      // For product with variants - check attributes and options
      const validAttrs = variantAttributes.filter(
        (a) => a.name && a.options.some((o) => o.trim()),
      );
      if (validAttrs.length === 0) {
        missingFields.push("At least one attribute with options for variants");
      } else {
        // Check each attribute has at least one option
        for (const attr of validAttrs) {
          if (attr.options.filter((o) => o.trim()).length === 0) {
            missingFields.push(
              `Attribute "${attr.name || "Unnamed"}" needs at least one option`,
            );
          }
        }
      }

      // ✅ NEW: Validate the actual generated variant rows — SKU, MRP, and Pack Unit
      // are required per row (matches the red * shown in the Variants table).
      const variantsToCheck =
        generatedVariants.length > 0
          ? generatedVariants
          : generateVariantsFromAttributes();

      const newVariantErrors = {};

      if (variantsToCheck.length === 0 && validAttrs.length > 0) {
        missingFields.push("No variants generated");
      }

      variantsToCheck.forEach((v, idx) => {
        const label = v.itemName || `Variant ${idx + 1}`;
        const rowErrors = {};

        if (!v.sku?.trim()) {
          rowErrors.sku = true;
          missingFields.push(`SKU is required for "${label}"`);
        }
        if (!v.mrp || Number(v.mrp) <= 0) {
          rowErrors.mrp = true;
          missingFields.push(`MRP is required for "${label}"`);
        }
        if (!v.unit?.trim()) {
          rowErrors.unit = true;
          missingFields.push(`Pack Unit is required for "${label}"`);
        }

        if (Object.keys(rowErrors).length > 0) {
          newVariantErrors[v.id] = rowErrors;
        }
      });

      // ✅ Duplicate SKU check across variant rows
      const skuCounts = {};
      variantsToCheck.forEach((v) => {
        const sku = v.sku?.trim();
        if (sku) skuCounts[sku] = (skuCounts[sku] || 0) + 1;
      });
      Object.entries(skuCounts).forEach(([sku, count]) => {
        if (count > 1) {
          missingFields.push(`Duplicate SKU found: ${sku}`);
          variantsToCheck.forEach((v) => {
            if (v.sku?.trim() === sku) {
              newVariantErrors[v.id] = {
                ...(newVariantErrors[v.id] || {}),
                sku: true,
              };
            }
          });
        }
      });

      setVariantErrors(newVariantErrors);
    }

    // Check HSN Code if GST slab is > 0
    if (
      formData.gst_slab &&
      Number(formData.gst_slab) > 0 &&
      !formData.hsn_code?.trim()
    ) {
      missingFields.push("HSN Code (required for taxable products)");
      newTouched.hsn_code = true;
    }

    // Update touched state to show error styles
    setTouched(newTouched);

    return missingFields;
  }, [
    formData,
    selectedCategory,
    variantAttributes,
    touched,
    generatedVariants,
    generateVariantsFromAttributes,
  ]);
  const markProductStepComplete = useCallback(async () => {
    if (!vendorId) {
      console.warn("No vendorId available, skipping product step completion.");
      return;
    }
    try {
      await httpService.put(`/vendorregistration/updatesetup/${vendorId}`, {
        steps: [{ key: "product", completed: true }],
      });
      console.log("✅ Product step marked complete for vendor", vendorId);
    } catch (error) {
      console.error("❌ Failed to mark product step complete:", error);
    }
  }, [vendorId]);

  // ── Submit handler ──
  const handleSubmit = async () => {
    // Validate form
    const missingFields = validateFormAndGetErrors();

    // Check for any validation errors from the validate function
    const hasValidationErrors = Object.keys(errors).length > 0;

    if (missingFields.length > 0 || hasValidationErrors) {
      // Show toast with all missing fields
      if (missingFields.length > 0) {
        const errorMessage = `Please fill in the following required fields:\n• ${missingFields.join("\n• ")}`;
        toast.error(errorMessage, {
          duration: 5000,
          style: { whiteSpace: "pre-line" },
        });

        // Highlight missing fields
        highlightMissingFields(missingFields);
      }

      if (hasValidationErrors) {
        // const errorMessages = Object.entries(errors)
        //   .filter(([_, msg]) => msg)
        //   .map(([field, msg]) => `• ${msg}`);

        // if (errorMessages.length > 0) {
        //   toast.error(`Please fix the following errors:\n${errorMessages.join('\n')}`, {
        //     duration: 5000,
        //     style: { whiteSpace: 'pre-line' },
        //   });
        // }

        // Mark all error fields as touched to highlight them
        const errorFields = Object.keys(errors);
        if (errorFields.length > 0) {
          setTouched((prev) => {
            const newTouched = { ...prev };
            errorFields.forEach((field) => {
              newTouched[field] = true;
            });
            return newTouched;
          });
        }
      }

      // ✅ Scroll to first variant-row error cell if one exists, otherwise the
      // first general field error (name/category/sku/mrp/hsn)
      const firstVariantErrorCell = document.querySelector(
        '[data-variant-error="true"]',
      );
      const firstErrorField =
        firstVariantErrorCell ||
        document.querySelector(".border-red-500, .ring-red-500");
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
        firstErrorField.focus({ preventScroll: true });
      }

      return;
    }

    // Proceed with save
    setIsSaving(true);
    try {
      if (isEditMode) {
        await handleEditSubmit();
      } else {
        await handleAddSubmit();
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.message || "Failed to save product");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Add Submit (without guardSubmit) ──
  const handleAddSubmit = async () => {
    try {
      const payload = buildPayload({
        purchase_price: Number(formData.purchase_price) || 0,
        quantity_available: Number(formData.openingInventory) || 0,
      });

      console.log("📤 [ADD] Payload:", payload);

      if (forceZeroOpeningInventory) {
        payload.quantity_available = 0;
        if (Array.isArray(payload.variants)) {
          payload.variants = payload.variants.map((variant) => ({
            ...variant,
            opening_stock: 0,
          }));
        }
      }

      const response = await httpService.post(
        "/vendorproduct/addVendorProduct",
        payload,
      );

      if (response.status === true || response.code === 200) {
        toast.success(
          response.data?.variants?.length > 0
            ? `Product with ${response.data.variants.length} variants added successfully`
            : "Product added successfully",
        );
              await markProductStepComplete();

        onSave?.(response.data || { ...payload, _id: response.data?._id });
        onClose?.();
      } else {
        toast.error(response.message || "Failed to add product");
      }
    } catch (error) {
      console.error("Error details:", error.response?.data);
      toast.error(error.response?.data?.message || "Failed to add product");
      throw error;
    }
  };

  // ── Edit Submit (without guardSubmit) ──
  const handleEditSubmit = async () => {
    try {
      // ✅ Build the base payload
      const payload = buildPayload({
        gst_slab: Number(formData.gst_slab) || 0,
        inventory_id: inventoryId,
        mrp: Number(formData.mrp) || 0,
        purchase_price: Number(formData.purchase_price) || 0,
        sale_price: Number(formData.mrp) || 0,
        packaging: formData.packaging ? formData.packaging.trim() : null,
      });

      // ✅ ADD: Include opening inventory if it's been changed
      // The backend updateVendorProduct expects quantity_available
      if (
        formData.openingInventory !== undefined &&
        formData.openingInventory !== ""
      ) {
        payload.quantity_available = Number(formData.openingInventory) || 0;
      }

      const response = await httpService.put(
        `/vendorproduct/updateVendorProduct/${productData._id}`,
        payload,
      );

      onSave?.({
        _id: productData._id,
        inventory_id: inventoryId,
        ...payload,
        quantity_available: productData.quantity_available,
        quantity_reserved: productData.quantity_reserved,
        ...response.data,
      });
      toast.success("Product updated successfully");
      onClose();
    } catch (err) {
      console.error("Error updating product:", err);
      toast.error(err.response?.data?.message || "Failed to update Product");
      throw err;
    }
  };

  // ── Fetch helpers ──────────────────────────────────────────────────────────
  const fetchCategories = async () => {
    try {
      const res = await httpService.get(
        "/productcategory/getProductCategories",
      );
      if (res.status === true && res.code === 200 && Array.isArray(res.data)) {
        setCategories(res.data);
      } else {
        setCategories([]);
      }
    } catch {
      toast.error("Failed to load categories");
    }
  };

  const fetchAllProducts = async () => {
    try {
      const res = await httpService.get("/su/product/getProducts");
      if (res.code === 200) setAllProducts(res.data || []);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  const fetchVendorProducts = async () => {
    try {
      const res = await httpService.get("/vendorproduct/getvendorproducts");
      if (res.code === 200) {
        const products = res.data || [];
        setAllVendorProducts(products);
        const skus = products.map((p) => p.sku_number);
        setVendorSkus(skus);
        return skus;
      }
    } catch (err) {
      console.error("Error fetching vendor products:", err);
    }
    return [];
  };

  const fetchMasterProducts = async () => {
    try {
      const res = await httpService.get("/su/product/getProducts");
      if (res.code === 200) return res.data || [];
    } catch (err) {
      console.error("Error fetching master products:", err);
    }
    return [];
  };

  // ── Search ─────────────────────────────────────────────────────────────────
  const performSearch = useCallback(
    debounce(async (term) => {
      if (!term || term.length < 2) {
        setSearchResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const q = term.toLowerCase();
        const results = allProducts.filter((p) => {
          const nameMatch    = p.name?.toLowerCase().includes(q);
          const skuMatch     = p.sku_number?.toLowerCase().includes(q);
          const barcodeMatch = String(p.barcode || "").toLowerCase().includes(q);
          const variantMatch = p.variants?.some(
            (v) =>
              v.variant_name?.toLowerCase().includes(q) ||
              v.sku_number?.toLowerCase().includes(q) ||
              String(v.barcode || "").toLowerCase().includes(q),
          );
          return nameMatch || skuMatch || barcodeMatch || variantMatch;
        });
        const vendorSkusList = await fetchVendorProducts();
        const filtered = results.filter((p) => {
          if (p.has_variants) return true;
          return !vendorSkusList.includes(p.sku_number);
        });
        setSearchResults(filtered);
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [allProducts],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    performSearch(value);
  };

  const selectProduct = (product) => {
    setSelectedProduct((prev) => (prev?._id === product._id ? null : product));
    setSelectedVariants([]);
  };

  const toggleVariant = (variantId) => {
    setSelectedVariants((prev) =>
      prev.includes(variantId)
        ? prev.filter((id) => id !== variantId)
        : [...prev, variantId],
    );
  };

  // ── Add selected products/variants ────────────────────────────────────────
  const handleAddSelected = async () => {
    if (!selectedProduct) {
      toast.error("Please select a product");
      return;
    }
    if (selectedProduct.has_variants && selectedVariants.length === 0) {
      toast.error("Please select at least one variant");
      return;
    }
    setIsSaving(true);
    try {
      const vendorSkusList = await fetchVendorProducts();

      let brandId = null;
      if (selectedProduct.product_brand) {
        if (
          typeof selectedProduct.product_brand === "object" &&
          selectedProduct.product_brand._id
        ) {
          brandId = selectedProduct.product_brand._id;
        } else if (typeof selectedProduct.product_brand === "string") {
          if (selectedProduct.product_brand.match(/^[a-f\d]{24}$/i)) {
            brandId = selectedProduct.product_brand;
          }
        }
      } else if (selectedProduct.product_brand_id) {
        if (
          typeof selectedProduct.product_brand_id === "object" &&
          selectedProduct.product_brand_id._id
        ) {
          brandId = selectedProduct.product_brand_id._id;
        } else if (typeof selectedProduct.product_brand_id === "string") {
          brandId = selectedProduct.product_brand_id;
        }
      }

      if (selectedProduct.has_variants) {
        let addedCount = 0;
        for (const variantId of selectedVariants) {
          const variant = selectedProduct.variants.find(
            (v) => v._id === variantId,
          );
          if (!variant) continue;
          if (vendorSkusList.includes(variant.sku_number)) {
            toast.warning(`Variant "${variant.variant_name}" already added`);
            continue;
          }
          const payload = {
            variant_id: variantId,
            product_id: selectedProduct._id,
            name: selectedProduct.name,
            variant_name: variant.variant_name,
            sku_number: variant.sku_number,
            mrp: variant.mrp || 0,
            purchase_price: variant.mrp || 0,
            sale_price: variant.mrp || 0,
            opening_inventory: 0,
            product_category:
              selectedProduct.product_category?.map((c) => c._id || c) || [],
            product_subcategory:
              selectedProduct.product_subcategory?.map((s) => s._id || s) || [],
            images: selectedProduct.images || [],
            description: selectedProduct.description || "",
            unit: selectedProduct.unit || "",
            hsn_code: selectedProduct.hsn_code || "",
            gst_slab: selectedProduct.gst_slab || "",
            product_brand_id: brandId,
          };
          await httpService.post("/vendorproduct/addVendorProduct", payload);
          addedCount++;
        }
        if (addedCount > 0)
          toast.success(`${addedCount} variant(s) added successfully`);
      } else {
        if (vendorSkusList.includes(selectedProduct.sku_number)) {
          toast.warning("Product already added");
          return;
        }
        const payload = {
          product_id: selectedProduct._id,
          variant_id: null,
          name: selectedProduct.name,
          sku_number: selectedProduct.sku_number,
          mrp: selectedProduct.mrp || 0,
          purchase_price: selectedProduct.mrp || 0,
          sale_price: selectedProduct.mrp || 0,
          opening_inventory: 0,
          product_category:
            selectedProduct.product_category?.map((c) => c._id || c) || [],
          product_subcategory:
            selectedProduct.product_subcategory?.map((s) => s._id || s) || [],
          images: selectedProduct.images || [],
          description: selectedProduct.description || "",
          unit: selectedProduct.unit || "",
          hsn_code: selectedProduct.hsn_code || "",
          gst_slab: selectedProduct.gst_slab || "",
          product_brand_id: brandId,
        };
        await httpService.post("/vendorproduct/addVendorProduct", payload);
        toast.success("Product added successfully");
      }
      onSave?.();
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add product");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    fetchCategories();
    fetchAllProducts();
    fetchVendorProducts();
    fetchBrands();
    fetchManufacturers();
    fetchVendorTaxes();
    fetchVendorUnits();
    fetchVendorHsns();
    setHsnSearch("");
    setShowHsnDropdown(false);
    if (!isEditMode) {
      const scannedBarcode = String(initialBarcode || "").trim();
      // Directly open product addition form (browsing page removed)
      setCurrentStage(STAGES.NEW_PRODUCT_FORM);
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setSelectedProduct(null);
      setSelectedVariants([]);
      setSearchTerm(scannedBarcode ? "" : newProductName || "");
      setSearchResults([]);
      setVariantAttributes([]);
      setGeneratedVariants([]);
      setVariantErrors({}); // ✅ reset variant-row highlights when drawer (re)opens
      if (!newProductName && !scannedBarcode) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
    }
  }, [isOpen, isEditMode, newProductName, initialBarcode]);

  useEffect(() => {
    if (!isOpen || isEditMode) return;
    let cancelled = false;
    const scannedBarcode = String(initialBarcode || "").trim();

    const applyForm = async () => {
      const nextForm = {
        ...INITIAL_FORM,
        name: scannedBarcode ? "" : newProductName || "",
        barcode: scannedBarcode,
        openingInventory: forceZeroOpeningInventory ? "0" : "",
      };
      let categoryId = null;
      let subcategoryId = null;
      let brandName = "";
      let manufacturerName = "";
      let masterBrandId = null;
      let masterManufacturerId = null;

      if (scannedBarcode) {
        try {
          const res = await httpService.get("/su/product/getProducts");
          const match = findMasterByBarcode(res?.data || [], scannedBarcode);
          if (!cancelled && match?.product) {
            const { product, variant } = match;
            const variantLabel = variant?.variant_name
              ? `${product.name || ""} - ${variant.variant_name}`.trim()
              : product.name || "";
            nextForm.name = variantLabel;
            nextForm.description = product.description || "";
            nextForm.unit =
              variant?.sales_unit || variant?.quantity_unit || product.unit || "";
            nextForm.sku_number =
              variant?.sku_number ||
              (!product.has_variants ? product.sku_number || "" : "");
            nextForm.hsn_code = product.hsn_code || "";
            nextForm.gst_slab =
              product.gst_slab === 0 || product.gst_slab
                ? String(product.gst_slab)
                : "";
            nextForm.photos = product.images || [];
            nextForm.weight =
              product.weight === 0 || product.weight ? String(product.weight) : "";
            nextForm.length =
              product.length === 0 || product.length ? String(product.length) : "";
            nextForm.width =
              product.width === 0 || product.width ? String(product.width) : "";
            nextForm.height =
              product.height === 0 || product.height ? String(product.height) : "";
            nextForm.barcode = scannedBarcode;
            nextForm.mrp = "";
            nextForm.purchase_price = "";
            nextForm.openingInventory = forceZeroOpeningInventory ? "0" : "";
            categoryId = idOf(product.product_category?.[0]);
            subcategoryId = idOf(product.product_subcategory?.[0]);
            nextForm.category = categoryId || "";
            nextForm.subcategory = subcategoryId || "";
            brandName = product.product_brand?.name || "";
            manufacturerName = product.manufacturer?.name || "";
            masterBrandId = idOf(product.product_brand);
            masterManufacturerId = idOf(product.manufacturer);
            toast.info(
              "Matching product found in Product Master. Price and stock stay blank for this purchase.",
            );
          }
        } catch (err) {
          console.error("Product master barcode lookup failed:", err);
        }
      }

      if (cancelled) return;
      setFormData(nextForm);
      setSelectedCategory(categoryId);
      setSelectedSubcategory(subcategoryId);
      setTouched({});
      setErrors({});
      setVariantAttributes([]);
      setGeneratedVariants([]);
      setVariantErrors({});
      // If the master gave us a SKU, keep it — don't let auto-generator overwrite.
      setSkuManuallyEdited(!!nextForm.sku_number);

      if (brandName || manufacturerName || masterBrandId || masterManufacturerId) {
        const [brandList, manufacturerList] = await Promise.all([
          fetchBrands(),
          fetchManufacturers(),
        ]);
        if (cancelled) return;
        let brandHit = (brandList || []).find(
          (b) => b.name?.trim().toLowerCase() === brandName.trim().toLowerCase(),
        );
        let manufacturerHit = (manufacturerList || []).find(
          (m) =>
            m.name?.trim().toLowerCase() === manufacturerName.trim().toLowerCase(),
        );

        let brandId = brandHit?._id || null;
        let manufacturerId = manufacturerHit?._id || null;

        // Auto-create the vendor brand if it's missing
        if (!brandId && brandName) {
          try {
            const r = await httpService.post("/vendorproduct/addvendorbrand", {
              name: brandName,
              is_active: true,
            });
            const created = r.data?.data || r.data;
            brandId = created?._id || created?.data?._id;
            if (brandId) {
              setBrands((prev) => {
                if ((prev || []).some((b) => b._id === brandId)) return prev;
                return [
                  ...(prev || []),
                  { _id: brandId, name: brandName, is_active: true },
                ];
              });
            }
          } catch (e) {
            console.warn(
              "Auto-create vendor brand failed, falling back to master id",
              e,
            );
            brandId = masterBrandId;
          }
        }

        // Auto-create the vendor manufacturer if it's missing
        if (!manufacturerId && manufacturerName) {
          try {
            const r = await httpService.post(
              "/vendorproduct/addvendormanufacturer",
              { name: manufacturerName, is_active: true },
            );
            const created = r.data?.data || r.data;
            manufacturerId = created?._id || created?.data?._id;
            if (manufacturerId) {
              setManufacturers((prev) => {
                if ((prev || []).some((m) => m._id === manufacturerId)) {
                  return prev;
                }
                return [
                  ...(prev || []),
                  {
                    _id: manufacturerId,
                    name: manufacturerName,
                    is_active: true,
                  },
                ];
              });
            }
          } catch (e) {
            console.warn(
              "Auto-create vendor manufacturer failed, falling back to master id",
              e,
            );
            manufacturerId = masterManufacturerId;
          }
        }

        setFormData((prev) => ({
          ...prev,
          brand: brandId || masterBrandId || prev.brand,
          manufacturer: manufacturerId || masterManufacturerId || prev.manufacturer,
        }));
      }
    };

    applyForm();
    return () => {
      cancelled = true;
    };
  }, [isOpen, isEditMode, newProductName, initialBarcode, forceZeroOpeningInventory]);

  useEffect(() => {
    if (!isOpen || !isEditMode || !productData) return;
    const dim = parseDimension(productData.dimension || "");
    const hasVariants = productData.has_variants || false;

    // ✅ Fix: Extract brand ID correctly
    let brandId = null;
    if (productData.product_brand_id) {
      if (
        typeof productData.product_brand_id === "object" &&
        productData.product_brand_id._id
      ) {
        brandId = productData.product_brand_id._id;
      } else if (typeof productData.product_brand_id === "string") {
        brandId = productData.product_brand_id;
      } else if (productData.product_brand_id._id) {
        brandId = productData.product_brand_id._id;
      }
    }

    // ✅ Fix: Extract manufacturer ID correctly
    let mfgId = null;
    if (productData.manufacturer_id) {
      if (
        typeof productData.manufacturer_id === "object" &&
        productData.manufacturer_id._id
      ) {
        mfgId = productData.manufacturer_id._id;
      } else if (typeof productData.manufacturer_id === "string") {
        mfgId = productData.manufacturer_id;
      } else if (productData.manufacturer_id._id) {
        mfgId = productData.manufacturer_id._id;
      }
    }

    setHsnSearch("");
    setShowHsnDropdown(false);
    setFormData({
      name: productData.name || "",
      description: productData.description || "",
      unit: productData.unit || "",
      packaging: productData.packaging || "",
      sku_number: hasVariants ? "" : productData.sku_number || "",
      barcode: hasVariants ? "" : productData.barcode || "",
      brand: brandId, // ✅ Use extracted brand ID
      manufacturer: mfgId, // ✅ Use extracted manufacturer ID
      hsn_code: productData.hsn_code || "",
      photos: productData.images || [],
      mrp: hasVariants ? "" : productData.mrp || "",
      purchase_price: productData.purchase_price || "",
      gst_slab: productData.gst_slab || "",
      openingInventory:
        productData.quantity_available !== undefined &&
        productData.quantity_available !== null
          ? productData.quantity_available
          : "",
      length: dim.length,
      width: dim.width,
      height: dim.height,
      weight: productData.weight || "",
      colour: productData.colour || "",
      trackInventory: productData.track_inventory || false,
      restockAlert: productData.restock_alert || "",
      category: productData?.product_category?.[0]?._id || "",
      subcategory: productData?.product_subcategory?.[0]?._id || "",
      has_variants: hasVariants,
      is_expiry_tracked: productData.is_expiry_tracked || false,
    });

    setSelectedCategory(productData?.product_category?.[0]?._id || null);
    setSelectedSubcategory(productData?.product_subcategory?.[0]?._id || null);

    // Load variant attributes if product has them
    if (hasVariants && productData.variant_attributes) {
      setVariantAttributes(
        productData.variant_attributes.map((attr) => ({
          id:
            attr._id ||
            Date.now().toString() + Math.random().toString(36).substr(2, 5),
          name: attr.name,
          options: attr.options || [],
          is_variant_attribute: true,
        })),
      );
    }

    setTouched({});
    setErrors({});
    setVariantErrors({}); // ✅ reset variant-row highlights when loading a product for edit
  }, [isOpen, isEditMode, productData]);

  useEffect(() => {
    if (isEditMode || !selectedCategory || !selectedSubcategory) {
      setAvailableProducts([]);
      return;
    }
    const loadProducts = async () => {
      const [masterProducts, vendorSkusList] = await Promise.all([
        fetchMasterProducts(),
        fetchVendorProducts(),
      ]);
      const filtered = masterProducts.filter((p) => {
        const categoryMatch = p.product_category?.some(
          (c) => (c._id || c) === selectedCategory,
        );
        const subcategoryMatch = p.product_subcategory?.some(
          (s) => (s._id || s) === selectedSubcategory,
        );
        const notAlreadyAdded =
          p.has_variants || !vendorSkusList.includes(p.sku_number);
        return categoryMatch && subcategoryMatch && notAlreadyAdded;
      });
      setAvailableProducts(filtered);
    };
    loadProducts();
  }, [selectedCategory, selectedSubcategory, isEditMode]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        brandDropdownRef.current &&
        !brandDropdownRef.current.contains(event.target)
      ) {
        setShowBrandDropdown(false);
      }
      if (
        manufacturerDropdownRef.current &&
        !manufacturerDropdownRef.current.contains(event.target)
      ) {
        setShowManufacturerDropdown(false);
      }
      if (
        hsnDropdownRef.current &&
        !hsnDropdownRef.current.contains(event.target)
      ) {
        setShowHsnDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const goToCategoryBrowse = () => {
    setCurrentStage(STAGES.CATEGORY_SELECTION);
    setSelectedProduct(null);
    setSelectedVariants([]);
  };
  const goToNewProduct = () => {
    setCurrentStage(STAGES.NEW_PRODUCT_FORM);
    setSelectedProduct(null);
    setSelectedVariants([]);
    if (searchTerm.trim()) {
      setFormData((prev) => ({ ...prev, name: searchTerm.trim() }));
    }
  };
  const goToSearch = () => {
    setCurrentStage(STAGES.SEARCH);
    setSelectedProduct(null);
    setSelectedVariants([]);
  };

  const canAdd = useMemo(() => {
    if (!selectedProduct) return false;
    if (!selectedProduct.has_variants)
      return !vendorSkus.includes(selectedProduct.sku_number);
    return (
      selectedVariants.length > 0 &&
      selectedVariants.some(
        (id) =>
          !vendorSkus.includes(
            selectedProduct.variants.find((v) => v._id === id)?.sku_number,
          ),
      )
    );
  }, [selectedProduct, selectedVariants, vendorSkus]);

  // ── Quick Add Handler ──────────────────────────────────────────────────────
  const handleQuickAdd = async (product) => {
    setIsSaving(true);
    try {
      const vendorSkusList = await fetchVendorProducts();

      // Check if product already exists in vendor inventory
      if (vendorSkusList.includes(product.sku_number)) {
        toast.warning("Product already in your inventory");
        setIsSaving(false);
        return;
      }

      // Get brand ID
      let brandId = null;
      if (product.product_brand) {
        if (
          typeof product.product_brand === "object" &&
          product.product_brand._id
        ) {
          brandId = product.product_brand._id;
        } else if (typeof product.product_brand === "string") {
          if (product.product_brand.match(/^[a-f\d]{24}$/i)) {
            brandId = product.product_brand;
          }
        }
      } else if (product.product_brand_id) {
        if (
          typeof product.product_brand_id === "object" &&
          product.product_brand_id._id
        ) {
          brandId = product.product_brand_id._id;
        } else if (typeof product.product_brand_id === "string") {
          brandId = product.product_brand_id;
        }
      }

      // If product has variants, add the first variant or show a dialog
      if (product.has_variants && product.variants?.length > 0) {
        // Add the first variant by default
        const firstVariant = product.variants[0];

        const attributes = firstVariant.attributes || [];

        const payload = {
          variant_id: firstVariant._id,
          product_id: product._id,
          name: product.name,
          variant_name: firstVariant.variant_name,
          sku_number: firstVariant.sku_number,
          mrp: firstVariant.mrp || 0,
          purchase_price: firstVariant.mrp || 0,
          sale_price: firstVariant.mrp || 0,
          opening_inventory: 0,
          product_category:
            product.product_category?.map((c) => c._id || c) || [],
          product_subcategory:
            product.product_subcategory?.map((s) => s._id || s) || [],
          images: product.images || [],
          description: product.description || "",
          unit: product.unit || "",
          hsn_code: product.hsn_code || "",
          gst_slab: product.gst_slab || "",
          product_brand_id: brandId,
          attributes: attributes,
        };
        await httpService.post("/vendorproduct/addVendorProduct", payload);
        toast.success(
          `Added variant "${firstVariant.variant_name}" successfully`,
        );
      } else {
        // Single product without variants
        const payload = {
          product_id: product._id,
          variant_id: null,
          name: product.name,
          sku_number: product.sku_number,
          mrp: product.mrp || 0,
          purchase_price: product.mrp || 0,
          sale_price: product.mrp || 0,
          opening_inventory: 0,
          product_category:
            product.product_category?.map((c) => c._id || c) || [],
          product_subcategory:
            product.product_subcategory?.map((s) => s._id || s) || [],
          images: product.images || [],
          description: product.description || "",
          unit: product.unit || "",
          hsn_code: product.hsn_code || "",
          gst_slab: product.gst_slab || "",
          product_brand_id: brandId,
        };
        await httpService.post("/vendorproduct/addVendorProduct", payload);
        toast.success("Product added successfully");
      }

      // Refresh vendor products list
      await fetchVendorProducts();
      onSave?.();
    } catch (error) {
      console.error("Error adding product:", error);
      toast.error(error.response?.data?.message || "Failed to add product");
    } finally {
      setIsSaving(false);
    }
  };

  const resolveImageUrl = (img) => {
    if (!img) return "/icons/default-product.svg";
    if (img.startsWith("http")) return img;
    if (img.startsWith("blob:")) return img;
    return `https://flps2025.s3.ap-south-1.amazonaws.com/${img}`;
  };

  // ── Quick Add Variant Handler ──────────────────────────────────────────────
  const handleQuickAddVariant = async (product, variant) => {
    setIsSaving(true);
    try {
      const vendorSkusList = await fetchVendorProducts();

      // Check if variant already exists in vendor inventory
      if (vendorSkusList.includes(variant.sku_number)) {
        toast.warning("Variant already in your inventory");
        setIsSaving(false);
        return;
      }

      // Get brand ID
      let brandId = null;
      if (product.product_brand) {
        if (
          typeof product.product_brand === "object" &&
          product.product_brand._id
        ) {
          brandId = product.product_brand._id;
        } else if (typeof product.product_brand === "string") {
          if (product.product_brand.match(/^[a-f\d]{24}$/i)) {
            brandId = product.product_brand;
          }
        }
      } else if (product.product_brand_id) {
        if (
          typeof product.product_brand_id === "object" &&
          product.product_brand_id._id
        ) {
          brandId = product.product_brand_id._id;
        } else if (typeof product.product_brand_id === "string") {
          brandId = product.product_brand_id;
        }
      }

      const attributes = variant.attributes || [];

      const payload = {
        variant_id: variant._id,
        product_id: product._id,
        name: product.name,
        variant_name: variant.variant_name,
        sku_number: variant.sku_number,
        mrp: variant.mrp || 0,
        purchase_price: variant.mrp || 0,
        sale_price: variant.mrp || 0,
        opening_inventory: 0,
        product_category:
          product.product_category?.map((c) => c._id || c) || [],
        product_subcategory:
          product.product_subcategory?.map((s) => s._id || s) || [],
        images: product.images || [],
        description: product.description || "",
        unit: product.unit || "",
        hsn_code: product.hsn_code || "",
        gst_slab: product.gst_slab || "",
        product_brand_id: brandId,
        attributes: attributes,
      };

      await httpService.post("/vendorproduct/addVendorProduct", payload);
      toast.success(`Added variant "${variant.variant_name}" successfully`);

      // Refresh vendor products list
      await fetchVendorProducts();
      onSave?.();
    } catch (error) {
      console.error("Error adding variant:", error);
      toast.error(error.response?.data?.message || "Failed to add variant");
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Render: Search View ──────────────────────────────────────────────────
  const renderSearch = () => {
    // Get all vendor products for reference
    const vendorProductsData = allVendorProducts || [];

    // Filter vendor products based on search (products already in inventory)
    const vendorResults = vendorProductsData.filter((vp) => {
      const searchLower = searchTerm.toLowerCase();
      const nameMatch    = vp.name?.toLowerCase().includes(searchLower);
      const skuMatch     = vp.sku_number?.toLowerCase().includes(searchLower);
      const barcodeMatch = String(vp.barcode || "").toLowerCase().includes(searchLower);
      return nameMatch || skuMatch || barcodeMatch;
    });

    // Filter master products (not fully in vendor inventory)
    const masterResults = allProducts.filter((mp) => {
      // Apply search filter
      const searchLower = searchTerm.toLowerCase();
      const nameMatch = mp.name?.toLowerCase().includes(searchLower);
      const skuMatch = mp.sku_number?.toLowerCase().includes(searchLower);
      const variantMatch = mp.variants?.some(
        (v) =>
          v.variant_name?.toLowerCase().includes(searchLower) ||
          v.sku_number?.toLowerCase().includes(searchLower),
      );

      if (!(nameMatch || skuMatch || variantMatch)) return false;

      // If product has no variants
      if (!mp.has_variants || !mp.variants || mp.variants.length === 0) {
        // Check if already in vendor inventory
        const isInVendor = vendorProductsData.some((vp) => {
          const nameMatch =
            vp.name?.toLowerCase()?.trim() === mp.name?.toLowerCase()?.trim();
          const productIdMatch = vp.product_id === mp._id;
          return nameMatch || productIdMatch;
        });
        return !isInVendor;
      }

      // For products with variants - check if all variants are already added
      const allVariantsAdded = mp.variants.every((v) => {
        return vendorProductsData.some(
          (vp) =>
            vp.variant_id === v._id ||
            (vp.variant_name?.toLowerCase()?.trim() ===
              v.variant_name?.toLowerCase()?.trim() &&
              vp.product_id === mp._id),
        );
      });

      // If all variants are added, don't show the product
      if (allVariantsAdded) return false;

      return true;
    });

    return (
      <div className="p-4 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Search Products
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Search by product name or SKU to find existing products
          </p>
        </div>

        {/* Search Input */}
        <div className="relative">
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={async (e) => {
              if (e.key !== "Enter") return;
              e.preventDefault();
              const raw = searchTerm.trim();
              if (!raw) return;

              // ── Barcode path: lookup master, jump to form ──
              if (looksLikeBarcode(raw)) {
                try {
                  const res = await httpService.get("/su/product/getProducts");
                  const masterProducts = res?.data || [];
                  const match = findMasterByBarcode(masterProducts, raw);

                  if (match?.product) {
                    const { product, variant } = match;
                    const variantLabel = variant?.variant_name
                      ? `${product.name || ""} - ${variant.variant_name}`.trim()
                      : product.name || "";

                    const categoryId = idOf(product.product_category?.[0]);
                    const subcategoryId = idOf(product.product_subcategory?.[0]);
                    const brandName = product.product_brand?.name || "";
                    const manufacturerName = product.manufacturer?.name || "";
                    const masterBrandId = idOf(product.product_brand);
                    const masterManufacturerId = idOf(product.manufacturer);

                    setFormData((prev) => ({
                      ...prev,
                      name: variantLabel,
                      description: product.description || "",
                      unit:
                        variant?.sales_unit ||
                        variant?.quantity_unit ||
                        product.unit ||
                        "",
                      sku_number:
                        variant?.sku_number ||
                        (!product.has_variants ? product.sku_number || "" : ""),
                      hsn_code: product.hsn_code || "",
                      gst_slab:
                        product.gst_slab === 0 || product.gst_slab
                          ? String(product.gst_slab)
                          : "",
                      photos: product.images || [],
                      weight:
                        product.weight === 0 || product.weight
                          ? String(product.weight)
                          : "",
                      length:
                        product.length === 0 || product.length
                          ? String(product.length)
                          : "",
                      width:
                        product.width === 0 || product.width
                          ? String(product.width)
                          : "",
                      height:
                        product.height === 0 || product.height
                          ? String(product.height)
                          : "",
                      barcode: raw,
                      mrp: "",
                      purchase_price: "",
                      openingInventory: forceZeroOpeningInventory ? "0" : "",
                      category: categoryId || "",
                      subcategory: subcategoryId || "",
                    }));

                    setSelectedCategory(categoryId);
                    setSelectedSubcategory(subcategoryId);
                    setTouched({});
                    setErrors({});
                    setVariantErrors({});
                    setSkuManuallyEdited(
                      !!(
                        variant?.sku_number ||
                        (!product.has_variants && product.sku_number)
                      ),
                    );
                    setCurrentStage(STAGES.NEW_PRODUCT_FORM);
                    setSearchTerm("");

                    // ── Match brand/manufacturer by name; auto-create if missing ──
                    let brandId = null;
                    let manufacturerId = null;

                    if (brandName || manufacturerName) {
                      const [brandList, manufacturerList] = await Promise.all([
                        fetchBrands(),
                        fetchManufacturers(),
                      ]);
                      const brandHit = (brandList || []).find(
                        (b) =>
                          b.name?.trim().toLowerCase() ===
                          brandName.trim().toLowerCase(),
                      );
                      const manufacturerHit = (manufacturerList || []).find(
                        (m) =>
                          m.name?.trim().toLowerCase() ===
                          manufacturerName.trim().toLowerCase(),
                      );

                      if (brandHit) {
                        brandId = brandHit._id;
                      } else if (brandName) {
                        // Auto-create the vendor brand from the master
                        try {
                          const r = await httpService.post(
                            "/vendorproduct/addvendorbrand",
                            { name: brandName, is_active: true },
                          );
                          const created = r.data?.data || r.data;
                          brandId = created?._id || created?.data?._id;
                          if (brandId) {
                            // ✅ Push the new brand into local options immediately
                            // so the dropdown can render it this same render cycle
                            setBrands((prev) => {
                              if ((prev || []).some((b) => b._id === brandId)) {
                                return prev;
                              }
                              return [
                                ...(prev || []),
                                {
                                  _id: brandId,
                                  name: brandName,
                                  is_active: true,
                                },
                              ];
                            });
                          }
                        } catch (e) {
                          console.warn(
                            "Auto-create vendor brand failed, falling back to master id",
                            e,
                          );
                          brandId = masterBrandId;
                        }
                      }

                      if (manufacturerHit) {
                        manufacturerId = manufacturerHit._id;
                      } else if (manufacturerName) {
                        try {
                          const r = await httpService.post(
                            "/vendorproduct/addvendormanufacturer",
                            { name: manufacturerName, is_active: true },
                          );
                          const created = r.data?.data || r.data;
                          manufacturerId = created?._id || created?.data?._id;
                          if (manufacturerId) {
                            setManufacturers((prev) => {
                              if (
                                (prev || []).some(
                                  (m) => m._id === manufacturerId,
                                )
                              ) {
                                return prev;
                              }
                              return [
                                ...(prev || []),
                                {
                                  _id: manufacturerId,
                                  name: manufacturerName,
                                  is_active: true,
                                },
                              ];
                            });
                          }
                        } catch (e) {
                          console.warn(
                            "Auto-create vendor manufacturer failed, falling back to master id",
                            e,
                          );
                          manufacturerId = masterManufacturerId;
                        }
                      }

                      if (brandId || manufacturerId) {
                        setFormData((prev) => ({
                          ...prev,
                          brand: brandId || prev.brand,
                          manufacturer: manufacturerId || prev.manufacturer,
                        }));
                      }
                    }

                    toast.info(
                      "Matching product found in Product Master. Complete the form.",
                    );
                    return;
                  }
                } catch (err) {
                  console.error("Master barcode lookup failed:", err);
                }

                // No master match → open blank form with barcode prefilled
                setFormData((prev) => ({
                  ...prev,
                  name: "",
                  barcode: raw,
                  openingInventory: forceZeroOpeningInventory ? "0" : "",
                }));
                setSelectedCategory(null);
                setSelectedSubcategory(null);
                setTouched({});
                setErrors({});
                setVariantErrors({});
                setSkuManuallyEdited(false);
                setCurrentStage(STAGES.NEW_PRODUCT_FORM);
                setSearchTerm("");
                toast.info(
                  "No master match. Enter the product details.",
                );
                return;
              }

              // ── Non-barcode path: normal search happens via performSearch already ──
              // (typing continues to debounce-search as usual)
            }}
            placeholder="Search by product name or SKU..."
            className="w-full p-3 pl-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <HiSearch className="absolute left-3 top-3.5 text-gray-400 w-5 h-5" />
          {isSearching && (
            <div className="absolute right-3 top-3.5">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500" />
            </div>
          )}
        </div>

        {searchTerm.length >= 2 ? (
          <div className="space-y-6">
            {/* ── Vendor Products Section ── */}
            {vendorResults.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-green-700">
                    📦 Your Inventory
                  </span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {vendorResults.length}
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div className="space-y-2">
                  {vendorResults.map((product) => (
                    <div
                      key={product._id}
                      className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      {/* Product Image */}
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 mr-3">
                        {product.images?.length > 0 ? (
                          <img
                            src={resolveImageUrl(product.images[0])}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "/icons/default-product.svg";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {product.name}
                            {product.variant_name && (
                              <span className="text-gray-500 font-normal ml-1">
                                - {product.variant_name}
                              </span>
                            )}
                          </p>
                          <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full flex-shrink-0">
                            ✅ In Stock
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                          <span>SKU: {product.sku_number || "N/A"}</span>
                          <span>•</span>
                          <span>₹{product.mrp || product.sale_price || 0}</span>
                          <span>•</span>
                          <span>Stock: {product.quantity_available || 0}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Master Products Section ── */}
            {masterResults.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-blue-700">
                    📚 Quick Add Products
                  </span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {masterResults.length}
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div className="space-y-2">
                  {masterResults
                    .map((product) => {
                      // Check if this product has variants
                      const hasVariants =
                        product.has_variants && product.variants?.length > 0;

                      if (hasVariants) {
                        // For products with variants, show each variant that hasn't been added
                        const availableVariants = product.variants.filter(
                          (v) => {
                            return !vendorProductsData.some(
                              (vp) =>
                                vp.variant_id === v._id ||
                                (vp.variant_name?.toLowerCase()?.trim() ===
                                  v.variant_name?.toLowerCase()?.trim() &&
                                  vp.product_id === product._id),
                            );
                          },
                        );

                        if (availableVariants.length === 0) return null;

                        return availableVariants.map((variant) => (
                          <div
                            key={variant._id}
                            className="flex items-center p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            {/* Product Image */}
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 mr-3">
                              {product.images?.length > 0 ? (
                                <img
                                  src={resolveImageUrl(product.images[0])}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = "/icons/default-product.svg";
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                </div>
                              )}
                            </div>

                            {/* Product Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-800 truncate">
                                  {variant.variant_name}
                                </p>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                                <span>SKU: {variant.sku_number || "N/A"}</span>
                                <span>•</span>
                                <span>₹{variant.mrp || 0}</span>
                                <span>•</span>
                                <span>
                                  {variant.sales_unit || product.unit || "N/A"}
                                </span>
                              </div>
                            </div>

                            {/* Quick Add Button - Just "Add" without "Variant" label */}
                            <button
                              onClick={() =>
                                handleQuickAddVariant(product, variant)
                              }
                              disabled={isSaving}
                              className="ml-3 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <HiPlus className="w-3 h-3" />
                              Add
                            </button>
                          </div>
                        ));
                      } else {
                        // For products without variants (single product)
                        return (
                          <div
                            key={product._id}
                            className="flex items-center p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            {/* Product Image */}
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 mr-3">
                              {product.images?.length > 0 ? (
                                <img
                                  src={resolveImageUrl(product.images[0])}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = "/icons/default-product.svg";
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                </div>
                              )}
                            </div>

                            {/* Product Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-800 truncate">
                                  {product.name}
                                  {product.variant_name && (
                                    <span className="text-gray-500 font-normal ml-1">
                                      - {product.variant_name}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                                <span>SKU: {product.sku_number || "N/A"}</span>
                                <span>•</span>
                                <span>₹{product.mrp || 0}</span>
                                <span>•</span>
                                <span>{product.unit || "N/A"}</span>
                              </div>
                            </div>

                            {/* Quick Add Button */}
                            <button
                              onClick={() => handleQuickAdd(product)}
                              disabled={isSaving}
                              className="ml-3 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <HiPlus className="w-3 h-3" />
                              Add
                            </button>
                          </div>
                        );
                      }
                    })
                    .filter(Boolean)}
                </div>
              </div>
            )}

            {/* No Results */}
            {vendorResults.length === 0 &&
              masterResults.length === 0 &&
              !isSearching && (
                <div className="text-center py-10">
                  <p className="text-gray-500 font-medium">No products found</p>
                  <p className="text-sm text-gray-400 mt-1 mb-4">
                    Try a different search or browse by category
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={goToCategoryBrowse}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                    >
                      Browse Categories
                    </button>
                    <button
                      onClick={goToNewProduct}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                    >
                      Add New Product
                    </button>
                  </div>
                </div>
              )}
          </div>
        ) : searchTerm.length > 0 ? (
          <p className="text-center text-gray-400 text-sm py-4">
            Type at least 2 characters to search
          </p>
        ) : (
          <div className="text-center py-10">
            <p className="text-gray-400 text-sm">
              Search for a product to add to your inventory
            </p>
            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={goToCategoryBrowse}
                className="text-blue-600 text-sm hover:underline"
              >
                Browse by Category →
              </button>
              <button
                onClick={goToNewProduct}
                className="text-green-600 text-sm hover:underline"
              >
                + Add New Product
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };
  // ─── Render: Category Browse ──────────────────────────────────────────────
  const renderCategoryBrowse = () => {
    // Get all vendor products for reference
    const vendorProductsData = allVendorProducts || [];

    // Filter vendor products by category
    const vendorCategoryResults = vendorProductsData.filter((vp) => {
      const categoryMatch = vp.product_category?.some(
        (c) =>
          c._id === selectedCategory ||
          (typeof c === "string" && c === selectedCategory),
      );
      if (!categoryMatch) return false;

      // If subcategory is selected, filter by subcategory
      if (selectedSubcategory) {
        const subcategoryMatch = vp.product_subcategory?.some(
          (s) =>
            s._id === selectedSubcategory ||
            (typeof s === "string" && s === selectedSubcategory),
        );
        return subcategoryMatch;
      }
      return true;
    });

    // Get master products by category
    const masterCategoryResults = allProducts.filter((mp) => {
      const categoryMatch = mp.product_category?.some(
        (c) =>
          c._id === selectedCategory ||
          (typeof c === "string" && c === selectedCategory),
      );
      if (!categoryMatch) return false;

      // If subcategory is selected, filter by subcategory
      if (selectedSubcategory) {
        const subcategoryMatch = mp.product_subcategory?.some(
          (s) =>
            s._id === selectedSubcategory ||
            (typeof s === "string" && s === selectedSubcategory),
        );
        if (!subcategoryMatch) return false;
      }

      // For products with variants - check if all variants are already added
      if (mp.has_variants && mp.variants && mp.variants.length > 0) {
        const allVariantsAdded = mp.variants.every((v) => {
          return vendorProductsData.some(
            (vp) =>
              vp.variant_id === v._id ||
              (vp.variant_name?.toLowerCase()?.trim() ===
                v.variant_name?.toLowerCase()?.trim() &&
                vp.product_id === mp._id),
          );
        });
        if (allVariantsAdded) return false;

        // Check if at least one variant is available
        const hasAvailableVariant = mp.variants.some((v) => {
          return !vendorProductsData.some(
            (vp) =>
              vp.variant_id === v._id ||
              (vp.variant_name?.toLowerCase()?.trim() ===
                v.variant_name?.toLowerCase()?.trim() &&
                vp.product_id === mp._id),
          );
        });
        return hasAvailableVariant;
      }

      // For single products - check if already in vendor inventory
      const isInVendor = vendorProductsData.some((vp) => {
        const nameMatch =
          vp.name?.toLowerCase()?.trim() === mp.name?.toLowerCase()?.trim();
        const productIdMatch = vp.product_id === mp._id;
        return nameMatch || productIdMatch;
      });

      return !isInVendor;
    });

    return (
      <div className="p-4 space-y-4">
        <button
          onClick={goToSearch}
          className="text-blue-600 text-sm hover:underline flex items-center gap-1"
        >
          ← Back to Search
        </button>
        <h2 className="text-lg font-semibold">Browse by Category</h2>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Category">
            <select
              id="category"
              name="category"
              value={selectedCategory || ""}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSelectedSubcategory(null);
              }}
              className={inputCls(false)}
            >
              <option value="">Select Category</option>
              {categories.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Subcategory">
            <select
              value={selectedSubcategory || ""}
              onChange={(e) => setSelectedSubcategory(e.target.value || null)}
              disabled={!selectedCategory}
              className={`${inputCls(false)} ${!selectedCategory ? "bg-gray-100 cursor-not-allowed" : ""}`}
            >
              <option value="">All Subcategories</option>
              {subcategories.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                </option>
              ))}
            </select>
          </FormField>
        </div>

        {selectedCategory && (
          <div className="mt-4 space-y-6">
            {/* ── Vendor Products Section ── */}
            {vendorCategoryResults.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-green-700">
                    📦 Your Inventory
                  </span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {vendorCategoryResults.length}
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div className="space-y-2">
                  {vendorCategoryResults.map((product) => (
                    <div
                      key={product._id}
                      className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      {/* Product Image */}
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 mr-3">
                        {product.images?.length > 0 ? (
                          <img
                            src={resolveImageUrl(product.images[0])}
                            alt={product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "/icons/default-product.svg";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400">
                            <svg
                              className="w-6 h-6"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {product.name}
                            {product.variant_name && (
                              <span className="text-gray-500 font-normal ml-1">
                                - {product.variant_name}
                              </span>
                            )}
                          </p>
                          <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full flex-shrink-0">
                            ✅ In Stock
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                          <span>SKU: {product.sku_number || "N/A"}</span>
                          <span>•</span>
                          <span>₹{product.mrp || product.sale_price || 0}</span>
                          <span>•</span>
                          <span>Stock: {product.quantity_available || 0}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Master Products Section ── */}
            {masterCategoryResults.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-blue-700">
                    📚 Quick Add Products
                  </span>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {masterCategoryResults.length}
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div className="space-y-2">
                  {masterCategoryResults
                    .map((product) => {
                      const hasVariants =
                        product.has_variants && product.variants?.length > 0;

                      if (hasVariants) {
                        // Show each variant that hasn't been added
                        const availableVariants = product.variants.filter(
                          (v) => {
                            return !vendorProductsData.some(
                              (vp) =>
                                vp.variant_id === v._id ||
                                (vp.variant_name?.toLowerCase()?.trim() ===
                                  v.variant_name?.toLowerCase()?.trim() &&
                                  vp.product_id === product._id),
                            );
                          },
                        );

                        if (availableVariants.length === 0) return null;

                        return availableVariants.map((variant) => (
                          <div
                            key={variant._id}
                            className="flex items-center p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            {/* Product Image */}
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 mr-3">
                              {product.images?.length > 0 ? (
                                <img
                                  src={resolveImageUrl(product.images[0])}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = "/icons/default-product.svg";
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                </div>
                              )}
                            </div>

                            {/* Product Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-800 truncate">
                                  {variant.variant_name}
                                </p>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                                <span>SKU: {variant.sku_number || "N/A"}</span>
                                <span>•</span>
                                <span>₹{variant.mrp || 0}</span>
                                <span>•</span>
                                <span>
                                  {variant.sales_unit || product.unit || "N/A"}
                                </span>
                              </div>
                            </div>

                            <button
                              onClick={() =>
                                handleQuickAddVariant(product, variant)
                              }
                              disabled={isSaving}
                              className="ml-3 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <HiPlus className="w-3 h-3" />
                              Add
                            </button>
                          </div>
                        ));
                      } else {
                        // Single product without variants
                        return (
                          <div
                            key={product._id}
                            className="flex items-center p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            {/* Product Image */}
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 mr-3">
                              {product.images?.length > 0 ? (
                                <img
                                  src={resolveImageUrl(product.images[0])}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = "/icons/default-product.svg";
                                  }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                  <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                </div>
                              )}
                            </div>

                            {/* Product Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-800 truncate">
                                  {product.name}
                                  {product.variant_name && (
                                    <span className="text-gray-500 font-normal ml-1">
                                      - {product.variant_name}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                                <span>SKU: {product.sku_number || "N/A"}</span>
                                <span>•</span>
                                <span>₹{product.mrp || 0}</span>
                                <span>•</span>
                                <span>{product.unit || "N/A"}</span>
                              </div>
                            </div>

                            <button
                              onClick={() => handleQuickAdd(product)}
                              disabled={isSaving}
                              className="ml-3 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <HiPlus className="w-3 h-3" />
                              Add
                            </button>
                          </div>
                        );
                      }
                    })
                    .filter(Boolean)}
                </div>
              </div>
            )}

            {/* No Results */}
            {vendorCategoryResults.length === 0 &&
              masterCategoryResults.length === 0 && (
                <div className="text-center py-10 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 font-medium">
                    No products found in this category
                  </p>
                  <p className="text-sm text-gray-400 mt-1 mb-4">
                    {selectedSubcategory
                      ? `No products found in "${subcategories.find((s) => s._id === selectedSubcategory)?.name || "selected"}" subcategory`
                      : "Try selecting a different category or add a new product"}
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => {
                        setSelectedCategory(null);
                        setSelectedSubcategory(null);
                      }}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700"
                    >
                      Clear Filters
                    </button>
                    <button
                      onClick={goToNewProduct}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                    >
                      + Add New Product
                    </button>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>
    );
  };

  // ─── Render: Product List ─────────────────────────────────────────────────
  const renderProductList = () => {
    if (!selectedProduct) {
      return (
        <div className="p-4">
          <button
            onClick={goToCategoryBrowse}
            className="text-blue-600 text-sm hover:underline"
          >
            ← Back to Categories
          </button>
          <p className="text-center text-gray-500 py-8">No product selected</p>
        </div>
      );
    }
    const product = selectedProduct;
    return (
      <div className="p-4 space-y-4">
        <button
          onClick={goToCategoryBrowse}
          className="text-blue-600 text-sm hover:underline"
        >
          ← Back to Categories
        </button>
        <h2 className="text-lg font-semibold">{product.name}</h2>
        <div className="bg-gray-50 p-4 rounded-lg space-y-2">
          {!product.has_variants && (
            <>
              <p className="text-sm text-gray-600">
                <span className="font-medium">SKU:</span>{" "}
                {product.sku_number || "N/A"}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Unit:</span>{" "}
                {product.unit || "N/A"}
              </p>
            </>
          )}
          <p className="text-sm text-gray-600">
            <span className="font-medium">Category:</span>{" "}
            {product.product_category?.map((c) => c.name).join(", ") || "N/A"}
          </p>
          {product.description && (
            <p className="text-sm text-gray-600">
              <span className="font-medium">Description:</span>{" "}
              {product.description}
            </p>
          )}
        </div>
        {product.has_variants ? (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Select variants to add:
            </p>
            {product.variants?.length > 0 ? (
              <div className="space-y-1.5">
                {product.variants.map((variant) => (
                  <VariantRow
                    key={variant._id}
                    variant={variant}
                    isSelected={selectedVariants.includes(variant._id)}
                    isAlreadyAdded={vendorSkus.includes(variant.sku_number)}
                    onToggle={toggleVariant}
                  />
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 text-center py-3">
                No variants in master catalogue.
              </p>
            )}
          </div>
        ) : (
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">
              This product has no variants.
            </p>
          </div>
        )}
        <div className="flex gap-3 pt-4 border-t">
          <button
            onClick={() => {
              if (product.has_variants && selectedVariants.length === 0) {
                toast.error("Please select at least one variant");
                return;
              }
              handleAddSelected();
            }}
            disabled={!canAdd || isSaving}
            className={`px-4 py-2 rounded text-sm font-medium ${canAdd && !isSaving ? "bg-green-600 text-white hover:bg-green-700" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
          >
            {product.has_variants
              ? `Add ${selectedVariants.length} Variant${selectedVariants.length > 1 ? "s" : ""}`
              : "Add Product"}
          </button>
          <button
            onClick={goToCategoryBrowse}
            className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  // ─── Unit options based on category ──────────────────────────────────────────
  const renderForm = () => {
    const isValid = isFormValid();
    const isTaxable = formData.gst_slab && Number(formData.gst_slab) > 0;
    const validAttrs = variantAttributes.filter(
      (a) => a.name && a.options.some((o) => o.trim()),
    );
    const hasValidAttributes = validAttrs.length > 0;

    // Get unit options: strictly from API (vendorUnits)
    const unitOptions = vendorUnits.map((u) => u.name);

    return (
      <div className="p-4 space-y-6 pb-8">
        {/* {!isEditMode && (
          <button
            onClick={goToSearch}
            className="text-blue-600 text-sm hover:underline flex items-center gap-1"
          >
            ← Back to Search
          </button>
        )} */}

        {/* ── Two Column Layout: Left (Form Fields) | Right (Image Upload) ── */}
        <div className="grid grid-cols-3 gap-6">
          {/* LEFT COLUMN - Form Fields (2/3 width) */}
          <div className="col-span-2 space-y-3">
            {/* ── Product Name ── */}
            <div className="grid grid-cols-1 relative">
              <div className="flex items-center gap-2 mb-1.5">
                <label
                  htmlFor="name"
                  className="text-sm font-medium text-gray-700"
                >
                  Product Name <span className="text-red-500">*</span>
                </label>
                <InfoTooltip>
                  <p className="font-medium mb-1">Enter only the base name</p>
                  <p className="text-gray-300 mt-1">
                    e.g. instead of{" "}
                    <span className="text-white">
                      "Drools Dog Food 2kg Chicken"
                    </span>
                    , enter{" "}
                    <span className="text-white font-medium">
                      "Drools Dog Food"
                    </span>{" "}
                    here, then add Flavor (Meat, Chicken) and Weight (1kg, 2kg)
                    as attributes in contains variant.
                  </p>
                </InfoTooltip>
              </div>

              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                onFocus={() => setShowNameSuggestions(true)}
                onBlur={() => {
                  handleBlur("name");
                  setTimeout(() => setShowNameSuggestions(false), 150);
                }}
                placeholder="e.g. Drools Dog Food"
                className={inputCls(touched.name && errors.name)}
                autoComplete="off"
              />
              {touched.name && errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}

              {/* ── Possible duplicate warning */}
              {!isEditMode && showNameSuggestions && nameMatches.length > 0 && (
                <div className="absolute z-20 top-full mt-1 w-full bg-white border border-amber-300 rounded-md shadow-lg max-h-48 overflow-auto">
                  <p className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border-b border-amber-200">
                    ⚠️ Already in your inventory — check before adding a new one
                  </p>
                  {nameMatches.map((p) => (
                    <div
                      key={p._id}
                      className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between gap-2"
                    >
                      <span className="truncate">
                        {p.name}
                        {p.variant_name && (
                          <span className="text-gray-500 font-normal">
                            {" "}
                            - {p.variant_name}
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        SKU: {p.sku_number || "N/A"} · ₹{p.mrp || 0}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Category / Subcategory ── */}
            <div className="grid grid-cols-1">
              <FormField label="Category">
                <select
                  value={selectedCategory || ""}
                  onChange={(e) => {
                    const catId = e.target.value;
                    setSelectedCategory(catId);
                    // setSelectedSubcategory(null);
                    setFormData((p) => ({
                      ...p,
                      category: catId,
                      // subcategory: "",
                    }));
                  }}
                  className={inputCls(false)}
                >
                  <option value="">Select Category (Optional)</option>
                  {categories.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </FormField>

              {/* <FormField label="Subcategory">
                <div className="relative">
                  <button
                    type="button"
                    className={`w-full p-2 border border-gray-300 rounded-md text-sm text-left flex justify-between items-center bg-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    onClick={() => setShowSubCatDropdown((p) => !p)}
                    disabled={!selectedCategory}
                  >
                    <span
                      className={
                        !selectedSubcategory ? "text-gray-400" : "text-gray-700"
                      }
                    >
                      {selectedSubcategory
                        ? subcategories.find(
                            (s) => s._id === selectedSubcategory,
                          )?.name || "Select Subcategory"
                        : "Select Subcategory"}
                    </span>
                    <span className="text-gray-400 text-xs">▼</span>
                  </button>
                  {showSubCatDropdown && subcategories.length > 0 && (
                    <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow mt-1 max-h-48 overflow-auto">
                      {subcategories.map((sub) => (
                        <label
                          key={sub._id}
                          className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                        >
                          <input
                            type="radio"
                            checked={selectedSubcategory === sub._id}
                            onChange={() => {
                              setSelectedSubcategory(sub._id);
                              setShowSubCatDropdown(false);
                              setFormData((p) => ({
                                ...p,
                                subcategory: sub._id,
                              }));
                            }}
                            className="mr-2 accent-blue-600"
                          />
                          {sub.name}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </FormField> */}
            </div>

            {/* ── Brand & Manufacturer ── */}
            <div className="grid grid-cols-2 gap-3">
              <SearchableDropdown
                ref={brandDropdownRef}
                label="Brand"
                value={formData.brand}
                onChange={(id) =>
                  setFormData((prev) => ({ ...prev, brand: id }))
                }
                options={brands}
                searchValue={brandSearch}
                setSearchValue={setBrandSearch}
                showDropdown={showBrandDropdown}
                setShowDropdown={setShowBrandDropdown}
                loading={loadingBrands}
                onCreate={handleCreateBrand}
                onDelete={handleDeleteBrand}
                isCreating={creatingBrand}
                placeholder="Search or create brand..."
                createLabel="Create Brand"
                noResultsText="No brand found"
                inputClassName={inputCls(false)}
              />

              <SearchableDropdown
                ref={manufacturerDropdownRef}
                label="Manufacturer"
                value={formData.manufacturer}
                onChange={(id) =>
                  setFormData((prev) => ({ ...prev, manufacturer: id }))
                }
                options={manufacturers}
                searchValue={manufacturerSearch}
                setSearchValue={setManufacturerSearch}
                showDropdown={showManufacturerDropdown}
                setShowDropdown={setShowManufacturerDropdown}
                loading={loadingManufacturers}
                onCreate={handleCreateManufacturer}
                onDelete={handleDeleteManufacturer}
                isCreating={creatingManufacturer}
                placeholder="Search or create manufacturer..."
                createLabel="Create Manufacturer"
                noResultsText="No manufacturer found"
                inputClassName={inputCls(false)}
              />
            </div>

            {/* ── HSN Code & GST Slab ── */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <SearchableDropdown
                  ref={hsnDropdownRef}
                  label="HSN Code"
                  required
                  value={formData.hsn_code}
                  onChange={(val) => {
                    handleHsnChange(val);
                    setHsnSearch("");
                  }}
                  options={hsnOptions}
                  searchValue={hsnSearch}
                  setSearchValue={setHsnSearch}
                  showDropdown={showHsnDropdown}
                  setShowDropdown={setShowHsnDropdown}
                  loading={loadingHsns}
                  onCreate={(val) => {
                    handleHsnChange(val);
                    setShowHsnDropdown(false);
                    setHsnSearch("");
                  }}
                  placeholder="Search HSN code or description..."
                  createLabel="Use HSN Code"
                  noResultsText="No matching HSN code"
                  inputClassName={inputCls(touched.hsn_code && errors.hsn_code)}
                />
                {touched.hsn_code && errors.hsn_code && (
                  <p className="text-xs text-red-500 mt-1">{errors.hsn_code}</p>
                )}
              </div>

              <FormField label="GST Slab" required error={touched.gst_slab && errors.gst_slab}>
                <select
                  value={formData.gst_slab}
                  onChange={(e) => handleChange("gst_slab", e.target.value)}
                  onBlur={() => handleBlur("gst_slab")}
                  className={inputCls(touched.gst_slab && errors.gst_slab)}
                >
                  <option value="">Select GST Slab</option>
                  {vendorTaxes.map((t) => (
                    <option key={t._id || t.rate} value={t.rate}>
                      {t.name || `GST ${t.rate}%`}
                    </option>
                  ))}
                </select>
                {touched.gst_slab && errors.gst_slab && (
                  <p className="text-xs text-red-500 mt-1">{errors.gst_slab}</p>
                )}
              </FormField>
            </div>
          </div>

          {/* RIGHT COLUMN - Image Upload (1/3 width) */}
          <div className="col-span-1">
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 h-full">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Product Images (Upto 10 Images)
              </h3>
              <FileUpload
                uploadEndpoint="/upload/fileUpload"
                acceptedTypes={["image/png", "image/jpeg", "image/gif"]}
                multiple
                maxFiles={10}
                moduleName="vendors"
                docType="products/images"
                existingUrls={formData.photos || []}
                onUploadComplete={(urls) =>
                  setFormData((p) => ({ ...p, photos: urls }))
                }
              />
            </div>
          </div>
        </div>

        {/* ── Description ── (Full width, below the two-column layout) */}
        {/* <div className="grid grid-cols-1">
          <FormField label="Description">
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Enter description"
              rows={2}
              className={`${inputCls(false)} resize-none`}
            />
          </FormField>
        </div> */}

        {/* ── Has Variants toggle ── (Moved closer to left) */}
        {!isEditMode && (
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-gray-700">Item Type</p>
                <InfoTooltip>
                  <p className="font-medium mb-1">
                    Choose if this product has different variations.
                  </p>
                </InfoTooltip>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="has_variants"
                    checked={!formData.has_variants}
                    onChange={() => handleToggleVariants(false)}
                    className="w-4 h-4 accent-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Single Item
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="has_variants"
                    checked={formData.has_variants}
                    onChange={() => handleToggleVariants(true)}
                    className="w-4 h-4 accent-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Contains Variants
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* ── Attribute & Options Manager (shown when Contains Variants is selected) ── */}
        {formData.has_variants && (
          <div className="pt-4 border-t border-gray-200">
            {/* Attribute & Options Manager */}
            <AttributeManager
              attributes={variantAttributes}
              onAttributesChange={setVariantAttributes}
            />

            {/* Variant Preview - Show generated variants */}
            {hasValidAttributes && generatedVariants.length > 0 && (
              <div className="mt-6">
                {/* Variants Table */}
                <VariantTable
                  variants={generatedVariants}
                  onVariantsChange={setGeneratedVariants}
                  attributes={variantAttributes}
                  productName={formData.name || "Product"}
                  inputClass={inputCls}
                  errors={errors}
                  variantErrors={variantErrors}
                  unitOptions={unitOptions}
                />
              </div>
            )}
          </div>
        )}

        {/* ── Unit & Packaging Details (only for non-variant products) ── */}
        {!formData.has_variants && (
          <div className="grid grid-cols-2 gap-4 relative z-10">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Pack Unit <span className="text-red-500">*</span>
                </label>
                <InfoTooltip>
                  <p className="font-medium mb-1">What is Pack Unit?</p>
                  <p className="text-gray-300">
                    This defines how the product is sold or packed as a single
                    unit. For example:
                  </p>
                  <ul className="text-gray-300 mt-1 list-disc list-inside text-xs">
                    <li>Medicine: Strip, Vial, Bottle</li>
                    <li>Food: Pack, Box, Can, Bag</li>
                    <li>Toys: Piece, Set, Pack</li>
                    <li>Grooming: Bottle, Tube, Jar</li>
                  </ul>
                </InfoTooltip>
              </div>
              <select
                name="unit"
                id="unit"
                value={formData.unit}
                onChange={(e) => handleChange("unit", e.target.value)}
                className={inputCls(touched.unit && errors.unit)}
              >
                <option value="">Select Unit</option>
                {formData.unit && !unitOptions.includes(formData.unit) && (
                  <option value={formData.unit}>
                    {formData.unit.charAt(0).toUpperCase() +
                      formData.unit.slice(1)}
                  </option>
                )}
                {unitOptions.map((u) => (
                  <option key={u} value={u}>
                    {u.charAt(0).toUpperCase() + u.slice(1)}
                  </option>
                ))}
              </select>
              {unitOptions.length > 0 && (
                <p className="text-xs text-gray-400 mt-1">
                  Available units from Unit Master
                </p>
              )}
              {touched.unit && errors.unit && (
                <p className="text-xs text-red-500 mt-1">{errors.unit}</p>
              )}
            </div>

            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-medium text-gray-700">
                  Packaging Details
                </label>
                <InfoTooltip>
                  <p className="font-medium mb-1">Packaging (Weight / Quantity)</p>
                  <p className="text-gray-300">
                    Specify the weight, volume, or count of the package, e.g.,{" "}
                    <strong>750ml</strong>, <strong>250 gm</strong>,{" "}
                    <strong>1.2 kg</strong>, <strong>10 tablets</strong>.
                  </p>
                </InfoTooltip>
              </div>
              <input
                id="packaging"
                type="text"
                value={formData.packaging || ""}
                onChange={(e) => handleChange("packaging", e.target.value)}
                placeholder="e.g. 750ml, 250 gm, 1.2 kg"
                className={inputCls(false)}
              />
              <p className="text-xs text-gray-400 mt-1">
                E.g. 750ml, 250 gm (keeps product title clean)
              </p>
            </div>
          </div>
        )}

        {/* ── SKU / Barcode / MRP (only for non-variant products) ── */}
        {!formData.has_variants && (
          <div className="grid grid-cols-3 gap-4">
            <FormField
              label="SKU Number"
              required
              error={touched.sku_number && errors.sku_number}
            >
              <input
                id="sku_number"
                type="text"
                value={formData.sku_number}
                onChange={(e) => {
                  setSkuManuallyEdited(true);
                  handleChange("sku_number", e.target.value);
                }}
                onBlur={() => handleBlur("sku_number")}
                placeholder="e.g. PROD-001"
                className={inputCls(touched.sku_number && errors.sku_number)}
              />
              <p className="text-xs text-gray-400 mt-1">
                Auto-generated from the product name — edit if you want a custom
                SKU
              </p>
            </FormField>

            <FormField label="Barcode">
              <input
                type="text"
                value={formData.barcode}
                onChange={(e) => handleChange("barcode", e.target.value)}
                placeholder="Barcode (optional)"
                className={inputCls(false)}
              />
            </FormField>

            <FormField
              label="MRP (₹)"
              required
              error={touched.mrp && errors.mrp}
            >
              <input
                id="mrp"
                type="number"
                value={formData.mrp}
                onChange={(e) => handleChange("mrp", e.target.value)}
                onBlur={() => handleBlur("mrp")}
                placeholder="0.00"
                min="0.01"
                step="0.01"
                className={inputCls(touched.mrp && errors.mrp)}
              />
            </FormField>
          </div>
        )}

        {/* ── Opening Stock, Expiry Tracking, Restock Alert (Single Line) ── */}
        {!formData.has_variants && (
          <div className="grid grid-cols-3 gap-4">
            {/* Opening Stock */}
            <FormField label="Opening Stock">
              <input
                type="number"
                value={forceZeroOpeningInventory ? "0" : formData.openingInventory}
                onChange={(e) =>
                  handleChange("openingInventory", e.target.value)
                }
                placeholder="Opening quantity"
                min="0"
                step="1"
                disabled={forceZeroOpeningInventory}
                className={inputCls(false)}
              />
              {forceZeroOpeningInventory && (
                <p className="text-xs text-gray-400 mt-1">
                  Stock is added from the purchase bill quantity, not opening stock.
                </p>
              )}
            </FormField>

            {/* Expiry Tracking - Toggle only */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Expiry Tracking?
                </label>
                <InfoTooltip>
                  <p className="font-medium mb-1">What is expiry tracking?</p>
                  <p className="text-gray-300">
                    e.g. medicines, food, cosmetics. If off, stock is sold
                    oldest-purchased-first (FIFO). If on, stock is sold
                    earliest-expiry-first (FEFO) and Expiry Date becomes
                    required at purchase.
                  </p>
                </InfoTooltip>
              </div>
              <div className="flex items-center">
                <ToggleSwitch
                  isActive={formData.is_expiry_tracked}
                  onChange={(value) => handleChange("is_expiry_tracked", value)}
                />
              </div>
            </div>

            {/* Restock Alert - Checkbox + Threshold */}
            {/* <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Restock Alert?
                </label>
                <InfoTooltip>
                  <p className="font-medium mb-1">What is Restock Alert?</p>
                  <p className="text-gray-300">
                    Get notified when stock falls below the threshold quantity.
                  </p>
                </InfoTooltip>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="trackInventory"
                  checked={formData.trackInventory}
                  onChange={(e) =>
                    handleChange("trackInventory", e.target.checked)
                  }
                  className="w-4 h-4 accent-blue-600 cursor-pointer"
                />
                {formData.trackInventory && (
                  <input
                    type="text"
                    value={formData.restockAlert}
                    onChange={(e) =>
                      handleChange("restockAlert", e.target.value)
                    }
                    placeholder="Threshold"
                    className="flex-1 p-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-20"
                  />
                )}
              </div>
            </div> */}
          </div>
        )}

        {/* ── Actions ── */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              isSaving
                ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
            disabled={isSaving} // Only disable when saving
          >
            {isSaving
              ? "Saving..."
              : isEditMode
                ? "Update Product"
                : "Save Product"}
          </button>
        </div>
      </div>
    );
  };

  // ─── Main Render ────────────────────────────────────────────────────────────
  return (
    <div
      className={`fixed inset-0 z-50 transition-all duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-40 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        className={`absolute right-0 top-0 h-full w-full lg:w-3/4 bg-white shadow-xl transform transition-transform duration-300 ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 p-4 border-b bg-white shadow-sm">
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <HiArrowSmLeft className="w-7 h-7" />
          </button>
          <h2 className="text-xl font-bold text-blue-900">
            {isEditMode ? "Edit Product" : "Add Product"}
          </h2>
          <span className="ml-auto text-xs text-gray-400">
            {currentStage === STAGES.SEARCH && "🔍 Search"}
            {currentStage === STAGES.CATEGORY_SELECTION && "📂 Browse"}
            {currentStage === STAGES.PRODUCT_LIST && "📋 Select"}
            {currentStage === STAGES.NEW_PRODUCT_FORM && "✏️ New"}
          </span>
        </div>

        {/* Content */}
        <div className="h-[calc(100vh-65px)] overflow-y-auto">
          {isEditMode ? (
            renderForm()
          ) : (
            <>
              {currentStage === STAGES.SEARCH && renderSearch()}
              {currentStage === STAGES.CATEGORY_SELECTION &&
                renderCategoryBrowse()}
              {currentStage === STAGES.PRODUCT_LIST && renderProductList()}
              {currentStage === STAGES.NEW_PRODUCT_FORM && renderForm()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDrawer;